/**
 * Shielded Transaction Client
 * 
 * Complete implementation for Zcash shielded transactions using:
 * - WebZjs Keys-and-Send WASM for PCZT creation/signing
 * - Wallet Scanner service for witness data and balance checking
 * - Proper per-pool balance tracking (Transparent, Sapling, Orchard, Ironwood)
 */

import { getWebZjsClient, WebZjsKeysAndSendClient, Pczt, pczt_sign } from './webzjs-keys-and-send-client-minimal';
import { createWalletScannerClient, WalletScannerClientType } from '../api/walletScannerClient';
import { installFetchInterceptor, cacheNotesForAddress, clearNoteCacheForAddress } from './fetch-interceptor';
import { extractMerklePathSiblings, hasFullMerklePathWitness, ORCHARD_MERKLE_DEPTH } from './merklePath';
import { ExtendedChain, ExtendedDerivationPath, toZcashOutputScript } from './zcashHelpers';
import { secp256k1 } from '@noble/curves/secp256k1';
import { HDKey } from '@scure/bip32';
import { mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english';
import { assertShieldedSendAvailable } from '../shielded-send-status';
// NOTE: WebWallet package not available yet - waiting for @chainsafe/webzjs-wallet npm package
// import { WebZjsWalletClient, mapWalletScannerNoteToOrchardSpend, initializeWasm, type OrchardSpend } from './webzjs-wallet-client';

export interface ShieldedBalance {
	transparent: bigint;
	sapling: bigint;
	orchard: bigint;
	/**
	 * The NU6.3 shielded pool. Ironwood reuses the Orchard address and key
	 * material, so the same UFVK finds both, but they are separate pools with
	 * separate commitment trees and nullifier sets — a note can only be spent
	 * from the tree it was minted into. Reporting them as one number made a
	 * migrated wallet look like it still had Orchard funds to migrate.
	 */
	ironwood: bigint;
	total: bigint;
	formatted: {
		transparent: string;
		sapling: string;
		orchard: string;
		ironwood: string;
		total: string;
	};
	scanProgress?: {
		currentBlock: number;
		/**
		 * Chain tip, from `progress.chainTip`. NOT `progress.latestHeight` —
		 * that is the scanner's coverage high-water mark, which trails the
		 * cursor by one block, so using it as the tip made every percentage
		 * come out over 100 and clamp to 99.9 from the first batch onwards.
		 */
		latestBlock: number;
		/** Highest block actually scanned so far (`progress.latestHeight`). */
		scannedHeight?: number;
		/** 'detecting-birthday' while the backwards walk runs, then 'scanning'. */
		phase?: string | null;
		/** Lowest block the backwards walk has reached. */
		detectFrom?: number | null;
		/** The floor the backwards walk is heading for (NU6, or NU5 if deep). */
		detectFloor?: number | null;
		blocksScanned: number;
		transactionsScanned: number;
		notesFound: number;
		/** Percent of the CURRENT scan range (birthday → tip); null while unknown. */
		percentComplete: number | null;
		isScanning: boolean;
		jobId?: string;
		birthdayHeight?: number;
		birthdayAutoDetected?: boolean;
	};
}

/** Shielded pools a note can live in. */
export type ShieldedPool = 'sapling' | 'orchard' | 'ironwood';

/**
 * Which pool a scanner note belongs to.
 *
 * The scanner tags every note (`"orchard"` / `"ironwood"` from the pool
 * scanner, `"sapling"` from the Sapling pass). Anything untagged predates
 * Ironwood and is Orchard by construction, which is also what the old
 * `pool === 'sapling' ? 'sapling' : 'orchard'` coercion assumed — that
 * coercion is now wrong, because it silently relabels Ironwood notes as
 * Orchard and they then fail to spend against the Orchard tree.
 */
export function normaliseNotePool(pool: unknown): ShieldedPool {
	const value = String(pool || '').toLowerCase();
	if (value === 'sapling') return 'sapling';
	if (value === 'ironwood') return 'ironwood';
	return 'orchard';
}

/**
 * Split a raw scanner note list into per-pool totals (zatoshis).
 *
 * The scanner returns Orchard and Ironwood notes in one list; summing it
 * wholesale reports every shielded note as Orchard.
 */
export function sumNotesByPool(notes: any[]): { orchard: bigint; ironwood: bigint } {
	let orchard = 0n;
	let ironwood = 0n;
	for (const note of notes || []) {
		const value = BigInt(note?.value || 0);
		if (normaliseNotePool(note?.pool) === 'ironwood') ironwood += value;
		else orchard += value;
	}
	return { orchard, ironwood };
}

export interface ShieldedNote {
	txHash: string;
	blockHeight: number;
	value: number; // zatoshis
	pool: ShieldedPool;
	nullifier?: string;
	commitment?: string;
	memo?: string;
}

export interface ShieldedTransactionOptions {
	mnemonic: string;
	seedBytes?: Uint8Array;
	preExportedUfvk?: string;
	/** Hex-encoded secp256k1 private key for transparent input signing (e.g. from vault master key export). Used when mnemonic is unavailable. */
	transparentPrivateKeyHex?: string;
	/**
	 * Callback to sign a transparent sighash via MPC (no raw key needed).
	 * Returns { signatureDerHex, pubkeyHex } where signatureDerHex is the
	 * DER-encoded ECDSA signature (without sighashType appended).
	 */
	transparentSignFunction?: (sighashHex: string) => Promise<{ signatureDerHex: string; pubkeyHex: string }>;
	/**
	 * Compressed secp256k1 public key (33 bytes, hex) behind `transparentAddress`.
	 *
	 * Since NU6.3 the server's transparent builder checks `hash160(pubkey)`
	 * against each input's scriptPubKey before it will accept the coin, and the
	 * key cannot be recovered from the script. It is derived automatically from
	 * a mnemonic or private key; MPC callers, whose signer only reveals the
	 * pubkey after signing, must supply it here to spend transparent inputs.
	 */
	transparentPublicKeyHex?: string;
	fromAddress: string; // unified address
	transparentAddress?: string; // transparent address for shielding
	toAddress: string;
	amountZec: number;
	sendAll?: boolean;
	memo?: string;
	accountIndex?: number;
	birthdayHeight?: number;
	autoDetectBirthday?: boolean;
	allowTransparentInputs?: boolean;
	skipShieldedScan?: boolean;
	/** When not `auto`, only notes from that shielded pool are used as inputs (phrase / seed sends). */
	shieldedInputPoolPreference?: 'auto' | ShieldedPool;
	commissionBps?: number;
	commissionUnifiedAddress?: string;
	onStatus?: (message: string) => void;
	/** Called when the fee exceeds the safe limit. Return true to proceed, false to abort. */
	onFeeWarning?: (details: { feeZatoshis: number; feeZec: string; reason: string }) => Promise<boolean>;
	/**
	 * Callback for FROST Orchard spend-auth signing via cosigner relay.
	 * When provided, Orchard actions are signed externally via the FROST ceremony
	 * instead of using seed-derived spending keys.
	 * Called with (sighashHex, randomiserHex) → returns the 64-byte RedPallas signature hex.
	 */
	orchardFrostSignFunction?: (sighashHex: string, randomiserHex: string, actionIndex?: number, totalActions?: number) => Promise<string>;
	/**
	 * Hex-encoded 32-byte FROST group_public key used by the co-signer.
	 * Forwarded to the server's apply-orchard-frost endpoint so it can check
	 * that this matches the `ak` stored in the PCZT's Orchard FVK before the
	 * (expensive) zk-prover runs. When it doesn't match, the signatures can
	 * never verify — the co-signer is using a different FROST keyshare
	 * bundle than the one that derived this UFVK.
	 */
	orchardFrostGroupPublicHex?: string;
	/**
	 * Sapling FROST threshold signing function — if present, used for Sapling spends
	 * instead of seed-derived keys. Called with (sighashHex, alphaHex) → returns signature hex.
	 */
	saplingFrostSignFunction?: (sighashHex: string, alphaHex: string) => Promise<string>;
	/**
	 * Optional abort signal. When aborted (e.g. the user presses Cancel) the
	 * shielded-transaction pipeline short-circuits — in-flight fetches are
	 * aborted and any long-running witness-scan poll loop exits immediately
	 * with a user-cancelled error rather than a "timed out" error.
	 */
	abortSignal?: AbortSignal;
}

// Note cache for reusing recent scan results
interface CachedNotes {
	notes: any[];
	timestamp: number;
	ufvk: string;
	birthdayHeight: number;
	hasWitnessData: boolean; // Track if notes include witness data
	latestHeight?: number;
}

// Global note cache (shared across all instances)
const noteCache = new Map<string, CachedNotes>();
const NOTE_CACHE_STORAGE_KEY = 'wb32_note_cache';
const NOTE_CACHE_VERSION = 4; // Increment to invalidate all cached notes (v4 = force memo re-extraction from scanner)
const NOTE_CACHE_TTL = 30 * 60 * 1000; // 30 minutes - reuse notes if scanned recently (longer for better UX)
const EMPTY_NOTE_CACHE_TTL = 2 * 60 * 1000; // 2 minutes for empty scans — short so real notes aren't masked by a stale empty result
// Allow the chain to advance up to CACHE_CHAIN_STALENESS_BLOCKS blocks beyond the
// cached tip before we consider the cache stale.  Zcash mines a block every ~75 s,
// so 20 blocks ≈ 25 minutes — plenty of headroom for the 30-minute TTL.
const CACHE_CHAIN_STALENESS_BLOCKS = 20;
const PENDING_NULLIFIER_TTL_MS = 60 * 60 * 1000; // 60 minutes
const PENDING_NULLIFIER_STORAGE_KEY = 'wb32_pending_orchard_nullifiers';
const pendingNullifiersByUfvk = new Map<string, Map<string, number>>();
let pendingNullifiersLoaded = false;
const ZATOSHIS_PER_ZEC = 100000000;
const DEFAULT_SHIELDED_FEE_ZATOSHIS = 10000;

const normaliseBlockHeight = (value: unknown): number | null => {
	const parsed = Number(value);
	if (!Number.isFinite(parsed) || parsed <= 0) {
		return null;
	}
	return Math.floor(parsed);
};

/**
 * Percent of a scan range from the scanner's cursor position.
 *
 * `chainTip` MUST be the tip (`job.progress.chainTip`), not the coverage
 * high-water mark (`job.progress.latestHeight`). The scan job sets
 * `currentStart = lastScannedBlock + 1`, so feeding the coverage mark in as
 * the tip makes the ratio exceed 1 on the very first batch and the readout
 * sits on the 99.9 clamp for the whole scan — the bug reported 31 Jul 2026.
 *
 * Returns null when the range is not yet knowable; clamped to [0, 99.9] so
 * "100%" is only ever reported by the caller on actual completion.
 */
export const computeScanPercent = (
	cursor: number,
	rangeStart: number,
	chainTip: number | null
): number | null => {
	if (chainTip === null || !Number.isFinite(chainTip)) return null;
	if (!(cursor > 0) || !(rangeStart >= 0)) return null;
	const rangeTotal = chainTip - rangeStart;
	if (!(rangeTotal > 0)) return null;
	const done = ((cursor - rangeStart) / rangeTotal) * 100;
	return Math.min(99.9, Math.max(0, Math.round(done * 10) / 10));
};

/**
 * Percent of the BACKWARDS birthday walk.
 *
 * Auto-detect walks down from the chain tip towards a floor (NU6, or NU5 on a
 * deep sweep) looking for the wallet's earliest note, and only then does the
 * forward scan start. That walk is minutes of work during which the job
 * publishes `phase: 'detecting-birthday'` and a descending window but no
 * `currentStart` — so the client showed nothing at all and the user watched a
 * dead progress bar. Progress here is how far DOWN from the tip the walk has
 * reached, which is why the subtraction runs the other way round.
 */
export const computeWalkPercent = (
	windowLow: number | null,
	chainTip: number | null,
	floor: number | null
): number | null => {
	if (windowLow === null || chainTip === null || floor === null) return null;
	if (!(chainTip > floor)) return null;
	const covered = chainTip - windowLow;
	if (!(covered >= 0)) return null;
	const done = (covered / (chainTip - floor)) * 100;
	return Math.min(99.9, Math.max(0, Math.round(done * 10) / 10));
};

// ─── ZIP-317 fee calculation ─────────────────────────────────────────────────
// See https://zips.z.cash/zip-0317
const ZIP317_MARGINAL_FEE = 5000;   // 5 000 zatoshis per logical action
const ZIP317_GRACE_ACTIONS = 2;     // first 2 actions are "free" (minimum fee = 10 000)
const ZIP317_P2PKH_STANDARD_INPUT_SIZE = 150;
const ZIP317_P2PKH_STANDARD_OUTPUT_SIZE = 34;

/**
 * Calculate the minimum fee required by ZIP-317 for a given transaction shape.
 *
 * logical_actions = max(transparent_inputs, transparent_outputs)
 *                 + orchard_actions
 *                 + ironwood_actions
 *                 + max(sapling_spends, sapling_outputs)
 *
 * fee = MARGINAL_FEE × max(GRACE_ACTIONS, logical_actions)
 *
 * The two shielded bundles count actions on different terms since NU6.3,
 * because they carry different cross-address flags — see
 * `BundleType::num_actions` in the orchard crate:
 *
 * * **Orchard** has cross-address transfers disabled, so a spend and an output
 *   can never share an action; each is paired with a fabricated zero-valued
 *   counterpart. The pool is frozen and takes no outputs at all now, so this is
 *   one action per spend.
 * * **Ironwood** permits them, so one action can carry both: the bundle costs
 *   `max(spends, outputs)`.
 *
 * Each bundle pads to a two-action minimum when non-empty; an empty bundle is
 * not produced, so a transaction that touches one pool is not charged for the
 * other.
 *
 * This must agree with `FeeShape` in the server's `pczt_builder.rs`. When the
 * client under-declares, the server raises the fee to its own floor and takes
 * the difference out of the change — and when there is no change to take it
 * from, refuses the build. Pinned by
 * `shielded-fee-model.test.ts` against the first mainnet migration.
 */
function calculateZip317Fee(params: {
	transparentInputs: number;
	transparentOutputs: number;
	/** Spends of frozen-pool Orchard notes. */
	orchardSpends: number;
	/** Spends of Ironwood notes. */
	ironwoodSpends?: number;
	/** Shielded outputs, including the assumed change output. All are Ironwood. */
	orchardOutputs: number;
	saplingSpends?: number;
	saplingOutputs?: number;
}): number {
	const {
		transparentInputs,
		transparentOutputs,
		orchardSpends,
		ironwoodSpends = 0,
		orchardOutputs,
		saplingSpends = 0,
		saplingOutputs = 0,
	} = params;

	const padded = (n: number) => (n === 0 ? 0 : Math.max(2, n));

	const orchardActions = padded(orchardSpends);
	const ironwoodActions = padded(Math.max(ironwoodSpends, orchardOutputs));

	const logicalActions =
		Math.max(transparentInputs, transparentOutputs)
		+ orchardActions
		+ ironwoodActions
		+ Math.max(saplingSpends, saplingOutputs);

	return ZIP317_MARGINAL_FEE * Math.max(ZIP317_GRACE_ACTIONS, logicalActions);
}

/** Split a selected note list into per-pool spend counts for the fee model. */
export function countSpendsByPool(notes: any[]): { orchard: number; ironwood: number } {
	let orchard = 0;
	let ironwood = 0;
	for (const note of notes || []) {
		if (normaliseNotePool(note?.pool) === 'ironwood') ironwood += 1;
		else orchard += 1;
	}
	return { orchard, ironwood };
}
const RPC_ENDPOINT = 'https://rpc.zcash.winbit32.com';
const RPC_BLOCK_HEIGHT_METHOD = 'getblockchaininfo';
const CONSENSUS_HEIGHT_OFFSET = 1; // next-block height aligns branch id for mining
const RPC_FETCH_TIMEOUT_MS = 20000;
const resolveEnvNumber = (primaryKey: string, secondaryKey: string, fallback: number): number => {
	const raw = process.env[primaryKey] || process.env[secondaryKey] || '';
	const parsed = Number(raw);
	if (Number.isFinite(parsed) && parsed > 0) {
		return parsed;
	}
	return fallback;
};
const PCZT_FETCH_TIMEOUT_MS = resolveEnvNumber(
	'REACT_APP_ZCASH_PCZT_TIMEOUT_MS',
	'ZCASH_PCZT_TIMEOUT_MS',
	600000
);
const DEFAULT_API_BASE_URL = 'https://api.zcash.winbit32.com/api';
const DEFAULT_SHIELDED_COMMISSION_BPS = resolveEnvNumber(
	'REACT_APP_ZCASH_SHIELDED_COMMISSION_BPS',
	'ZCASH_SHIELDED_COMMISSION_BPS',
	10
);
const DEFAULT_SHIELDED_COMMISSION_UA =
	process.env.REACT_APP_ZCASH_SHIELDED_COMMISSION_UA
	|| process.env.ZCASH_SHIELDED_COMMISSION_UA
	|| 'u1f35nskx9nx0mhccmytp4ycltj25f5s0cqulsnva9rufvmtpgja82cdggv9df99xll7qlf2u42w07q6qark4c9ss99kq40r4epslmwzcum5z3xdm30e2646d9uvepf0gn9stknyaf994lu30q9rqw7tplac8rxz7uldxtswe22wnud9szuztw444nxyvnf0cm9c8y66n5c6p8q0gf2l7';
const DUPLICATE_NULLIFIER_MARKERS = ['duplicate nullifier', 'double-spend'];
const TRANSPARENT_DUST_THRESHOLD_ZATOSHIS = 1000;
const MIN_CONFIRMED_UTXO_CONFIRMATIONS = 1;
const DEFAULT_ZCASH_API_KEY =
	process.env.REACT_APP_ZCASH_API_KEY
	|| process.env.ZCASH_API_KEY
	|| '';

const normaliseApiKey = (value?: string | null): string | undefined => {
	if (typeof value !== 'string') return undefined;
	const trimmed = value.trim();
	return trimmed.length > 0 ? trimmed : undefined;
};

// ─── Note-cache — IN-MEMORY ONLY (May 2026 privacy fix) ──────────────────
// Earlier builds persisted the entire shielded note set (UFVK keys,
// nullifiers, amounts, txids, memos) to `localStorage`. That was a
// straight privacy leak — anyone with read access to the browser's
// localStorage could enumerate the wallet's shielded history. The
// cache is now purely session-scoped: the page reload triggers a
// fresh scan rather than re-hydrating sensitive data from disk.
//
// `_persistNoteCache`, `_deleteCacheEntry` and the version constants
// are kept as no-ops / single source of truth for the in-memory map
// so the existing call sites keep working without touching every
// scanner code path. The legacy storage key is swept once per tab
// from `sweepLegacyShieldedLocalStorage()` (called from `App.js`).

/** Broadcast a custom event so other windows/hooks can refresh balances */
const _dispatchBalanceEvent = (ufvk: string) => {
	try {
		if (typeof window !== 'undefined') {
			window.dispatchEvent(new CustomEvent('zcash-balance-update', { detail: { ufvk } }));
		}
	} catch { /* ignore */ }
};

/** No-op since the note cache is in-memory only now. Kept so the
 *  existing scanner code paths don't need to be touched — every call
 *  to `_persistNoteCache()` simply ensures the in-memory `noteCache`
 *  is the source of truth and nothing reaches disk. */
const _persistNoteCache = () => {
	// Intentionally empty — see comment above.
};

/** Drop an entry from the in-memory cache. */
const _deleteCacheEntry = (key: string) => {
	noteCache.delete(key);
};

/**
 * Sweep legacy localStorage entries written by older builds. Removes
 * both the note cache and the pending-nullifier cache so a freshly
 * loaded UFVK can't appear to "already know" notes / nullifiers from
 * a previous session. Idempotent and safe to call from boot.
 */
let _legacyShieldedSweepDone = false;
export function sweepLegacyShieldedLocalStorage(): void {
	if (_legacyShieldedSweepDone) return;
	_legacyShieldedSweepDone = true;
	if (typeof localStorage === 'undefined') return;
	try {
		localStorage.removeItem(NOTE_CACHE_STORAGE_KEY);
		localStorage.removeItem(PENDING_NULLIFIER_STORAGE_KEY);
	} catch (err) {
		console.warn('[shielded-cache] legacy localStorage sweep failed:',
			(err as Error).message);
	}
}

type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = Parameters<typeof fetch>[1];

const getRawFetch = (): typeof fetch => {
	if (typeof window === 'undefined') {
		return fetch;
	}
	const windowAny = window as any;
	return windowAny.__WB32_FETCH_ORIGINAL__ || windowAny.__WB32_NATIVE_FETCH__ || windowAny.fetch;
};

const normaliseNullifierHex = (value: string | null | undefined): string | null => {
	if (typeof value !== 'string') return null;
	let hex = value.trim();
	if (!hex) return null;
	if (hex.startsWith('0x') || hex.startsWith('0X')) {
		hex = hex.slice(2);
	}
	hex = hex.replace(/[^0-9a-fA-F]/g, '');
	if (!hex) return null;
	return hex.toLowerCase();
};

/**
 * A stable identity for a note, so the same note found by two scans maps to
 * one cache entry. `note_nullifier` is the note's own nullifier (what a spend
 * reveals); `nullifier` is the nullifier of the action that *created* it.
 * Either is unique within a wallet, so both work as a key — but the two scans
 * must agree on which one they used, hence the fixed order here.
 */
export const noteIdentityKey = (note: any): string | null => {
	const nf = normaliseNullifierHex(
		note?.note_nullifier ?? note?.noteNullifier ?? note?.nullifier ?? note?.nf_hex
	);
	if (nf) return `nf:${nf}`;
	const commitment = normaliseNullifierHex(note?.commitment ?? note?.cmx ?? note?.cm);
	if (commitment) return `cm:${commitment}`;
	const position = note?.leaf_position ?? note?.position;
	return position === undefined || position === null ? null : `pos:${position}`;
};

/** Height of the block the note was received in, or null if unknown. */
const getNoteBlockHeight = (note: any): number | null => {
	const raw = note?.block_height ?? note?.blockHeight ?? note?.height;
	const height = typeof raw === 'string' ? Number(raw) : raw;
	return typeof height === 'number' && Number.isFinite(height) && height > 0 ? height : null;
};

/**
 * Drop cached notes that a completed scan no longer reports.
 *
 * The scanner merges its own cache and filters any note whose nullifier has
 * appeared on chain, so its result is authoritative for the range it covered.
 * A cached note that sits inside that range and is missing from the result has
 * been SPENT. Keeping it (the old union-only merge did) re-offers a spent note
 * to the next send and the broadcast is rejected as a double-spend.
 *
 * Notes outside the covered range are kept, so a shallow or narrow scan can
 * still never wipe notes it simply did not look for.
 */
export const retireNotesMissingFromScan = (
	cachedNotes: any[],
	scannedNotes: any[],
	coverageStart: number,
	coverageEnd: number | null | undefined
): { kept: any[]; retired: number } => {
	if (coverageEnd === null || coverageEnd === undefined || !Array.isArray(cachedNotes)) {
		return { kept: cachedNotes || [], retired: 0 };
	}
	// A scan that found nothing at all is far more likely to be a scanner
	// hiccup than a wallet that just went to zero, and wiping the cache on it
	// is the old "random 0 balance" bug. Retire only against a non-empty result.
	if (!Array.isArray(scannedNotes) || scannedNotes.length === 0) {
		return { kept: cachedNotes, retired: 0 };
	}
	const scannedKeys = new Set<string>();
	for (const note of scannedNotes || []) {
		const key = noteIdentityKey(note);
		if (key) scannedKeys.add(key);
	}
	const start = coverageStart > 0 ? coverageStart : 0;
	const kept: any[] = [];
	let retired = 0;
	for (const note of cachedNotes) {
		const key = noteIdentityKey(note);
		const height = getNoteBlockHeight(note);
		const inCoveredRange = height !== null && height >= start && height <= coverageEnd;
		if (key && inCoveredRange && !scannedKeys.has(key)) {
			retired++;
			continue;
		}
		kept.push(note);
	}
	return { kept, retired };
};

/**
 * Pull the nullifier out of a consensus double-spend rejection, e.g.
 * `duplicate nullifier: Nullifier(0x0f8e…)`. zcashd/zebra print the field
 * element byte-reversed relative to the wire encoding the scanner reports, so
 * both orders are returned and the caller matches on either.
 */
export const extractRejectedNullifiers = (message: string): string[] => {
	const out = new Set<string>();
	const pattern = /(?:nullifier[^0-9a-fA-F]{0,20})(?:0x)?([0-9a-fA-F]{64})/gi;
	let match: RegExpExecArray | null;
	while ((match = pattern.exec(message || '')) !== null) {
		const hex = match[1].toLowerCase();
		out.add(hex);
		const reversed = (hex.match(/../g) || []).reverse().join('');
		out.add(reversed);
	}
	return Array.from(out);
};

const normaliseMnemonic = (phrase: string): string =>
	(phrase || '').trim().replace(/\s+/g, ' ');

const bytesToHex = (bytes: Uint8Array): string =>
	Array.prototype.map.call(bytes, (b: number) => b.toString(16).padStart(2, '0')).join('');

const hexToBytes = (hex: string): Uint8Array => {
	if (!hex) return new Uint8Array();
	const cleaned = hex.startsWith('0x') || hex.startsWith('0X')
		? hex.slice(2)
		: hex;
	if (!/^[0-9a-fA-F]*$/.test(cleaned) || cleaned.length % 2 !== 0) {
		throw new Error('Invalid hex string');
	}
	const out = new Uint8Array(cleaned.length / 2);
	for (let i = 0; i < out.length; i++) {
		out[i] = parseInt(cleaned.slice(i * 2, i * 2 + 2), 16);
	}
	return out;
};

const normaliseScriptHex = (value?: string | null): string => {
	if (typeof value !== 'string') return '';
	let script = value.trim();
	if (!script) return '';
	if (script.startsWith('0x') || script.startsWith('0X')) {
		script = script.slice(2);
	}
	if (!/^[0-9a-fA-F]+$/.test(script) || script.length % 2 !== 0) {
		return '';
	}
	return script.toLowerCase();
};

const deriveTransparentKeyPair = (mnemonic: string) => {
	const normalised = normaliseMnemonic(mnemonic);
	if (!validateMnemonic(normalised, wordlist)) {
		throw new Error('Invalid mnemonic phrase provided');
	}
	const seed = mnemonicToSeedSync(normalised);
	const hdkey = (HDKey as any).fromMasterSeed(seed);
	const derivationPath = ExtendedDerivationPath[ExtendedChain.Zcash];
	const childKey = hdkey.derive(derivationPath);
	if (!childKey.privateKey) {
		throw new Error('Failed to derive transparent private key');
	}
	const privateKeyBytes =
		childKey.privateKey instanceof Uint8Array
			? childKey.privateKey
			: new Uint8Array(childKey.privateKey);
	const publicKeyBytes = secp256k1.getPublicKey(privateKeyBytes, true);
	return {
		privateKeyBytes,
		publicKeyBytes,
		privateKeyHex: bytesToHex(privateKeyBytes),
		publicKeyHex: bytesToHex(publicKeyBytes)
	};
};

const selectTransparentUtxos = (
	utxos: Array<{ satoshis: number }>,
	requiredZatoshis: number
) => {
	const sorted = [...utxos].sort((a, b) => a.satoshis - b.satoshis);
	const selected: Array<{ satoshis: number }> = [];
	let total = 0;

	for (const utxo of sorted) {
		selected.push(utxo);
		total += utxo.satoshis;
		if (total >= requiredZatoshis) {
			break;
		}
	}

	if (total < requiredZatoshis) {
		throw new Error('Insufficient transparent funds for shielding');
	}

	return { selected, total };
};

/**
 * Pending nullifiers are kept ONLY in-memory now. The previous build
 * persisted them to localStorage so a refresh kept the "this note is
 * being spent" hint alive, but the trade-off is a privacy leak:
 * nullifiers are wallet-identifying. Session-scoped is the safest
 * default; an active spend that survives a reload simply re-discovers
 * the conflict the next time the scanner runs against the LWS.
 */
const loadPendingNullifiers = (): void => {
	if (pendingNullifiersLoaded) return;
	pendingNullifiersLoaded = true;
	// No localStorage hydration: in-memory only.
};

const persistPendingNullifiers = (): void => {
	// No-op — in-memory only. Kept as a stub so call sites compile
	// and we have one place to switch to an opt-in encrypted store
	// later if we add a "remember scan progress in this browser"
	// preference.
};

const getPendingNullifiersForUfvk = (ufvk: string): Set<string> => {
	loadPendingNullifiers();
	const entries = pendingNullifiersByUfvk.get(ufvk);
	if (!entries) return new Set();
	const now = Date.now();
	let changed = false;
	for (const [nf, ts] of entries.entries()) {
		if (!Number.isFinite(ts) || now - ts > PENDING_NULLIFIER_TTL_MS) {
			entries.delete(nf);
			changed = true;
		}
	}
	if (entries.size === 0) {
		pendingNullifiersByUfvk.delete(ufvk);
		changed = true;
	}
	if (changed) {
		persistPendingNullifiers();
	}
	return new Set(entries.keys());
};

const markPendingNullifiersForUfvk = (ufvk: string, nullifiers: string[]): void => {
	loadPendingNullifiers();
	if (!Array.isArray(nullifiers) || nullifiers.length === 0) return;
	const entries = pendingNullifiersByUfvk.get(ufvk) || new Map<string, number>();
	const now = Date.now();
	let changed = false;
	for (const nf of nullifiers) {
		const normalised = normaliseNullifierHex(nf);
		if (!normalised) continue;
		entries.set(normalised, now);
		changed = true;
	}
	if (changed) {
		pendingNullifiersByUfvk.set(ufvk, entries);
		persistPendingNullifiers();
	}
};

const clearPendingNullifiersForUfvk = (ufvk: string, nullifiers?: string[]): void => {
	loadPendingNullifiers();
	const entries = pendingNullifiersByUfvk.get(ufvk);
	if (!entries) return;
	if (!Array.isArray(nullifiers) || nullifiers.length === 0) {
		pendingNullifiersByUfvk.delete(ufvk);
		persistPendingNullifiers();
		return;
	}
	let changed = false;
	for (const nf of nullifiers) {
		const normalised = normaliseNullifierHex(nf);
		if (!normalised) continue;
		if (entries.delete(normalised)) {
			changed = true;
		}
	}
	if (entries.size === 0) {
		pendingNullifiersByUfvk.delete(ufvk);
		changed = true;
	}
	if (changed) {
		persistPendingNullifiers();
	}
};

const fetchWithTimeout = async (input: FetchInput, init: FetchInit = {}, timeoutMs?: number): Promise<Response> => {
	const rawFetch = getRawFetch();
	if (!timeoutMs || timeoutMs <= 0 || typeof AbortController === 'undefined') {
		return rawFetch(input, init);
	}

	const controller = new AbortController();
	const originalSignal = init?.signal;
	if (originalSignal) {
		if (originalSignal.aborted) {
			controller.abort();
		} else {
			originalSignal.addEventListener('abort', () => controller.abort());
		}
	}

	const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
	try {
		return await rawFetch(input, { ...(init || {}), signal: controller.signal });
	} finally {
		clearTimeout(timeoutId);
	}
};

const parseRateLimitRetryMs = (response: Response): number | null => {
	const retryAfter = response.headers.get('Retry-After');
	if (retryAfter) {
		const retryAfterSeconds = Number(retryAfter);
		if (Number.isFinite(retryAfterSeconds)) {
			return Math.max(0, retryAfterSeconds * 1000);
		}
		const retryAfterDate = Date.parse(retryAfter);
		if (!Number.isNaN(retryAfterDate)) {
			return Math.max(0, retryAfterDate - Date.now());
		}
	}

	const resetHeader = response.headers.get('RateLimit-Reset');
	if (!resetHeader) {
		return null;
	}

	const resetValue = Number(resetHeader);
	if (!Number.isFinite(resetValue)) {
		return null;
	}

	// Interpret large values as epoch seconds; otherwise treat as delta seconds.
	if (resetValue > 1_000_000_000) {
		return Math.max(0, resetValue * 1000 - Date.now());
	}

	return Math.max(0, resetValue * 1000);
};

const safeParseJson = async (response: Response): Promise<any | null> => {
	try {
		return await response.json();
	} catch (_error) {
		return null;
	}
};

const fetchLatestBlockHeight = async (): Promise<number | null> => {
	try {
		const response = await fetchWithTimeout(
			RPC_ENDPOINT,
			{
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					jsonrpc: '1.0',
					id: 'latest-block',
					method: RPC_BLOCK_HEIGHT_METHOD,
					params: []
				})
			},
			RPC_FETCH_TIMEOUT_MS
		);
		const payload = await response.json();
		const height = Number(payload?.result?.blocks ?? payload?.result?.height ?? payload?.result);
		if (Number.isFinite(height) && height > 0) {
			return height;
		}
	} catch (error) {
		console.warn('⚠️ Failed to fetch latest block height:', error);
	}
	return null;
};

export class ShieldedTransactionClient {
	private webzjsClient: WebZjsKeysAndSendClient;
	private walletScanner: WalletScannerClientType;
	private network: string;
	private lightwalletdUrl: string;
	private apiBaseUrl: string;
	private walletScannerBaseUrl: string;
	private apiKey?: string;

	constructor(
		network: string = 'main',
		lightwalletdUrl: string = 'https://lightwalletd.zcash.winbit32.com',
		walletScannerUrl?: string,
		apiBaseUrl?: string,
		apiKey?: string
	) {
		this.network = network;
		this.lightwalletdUrl = lightwalletdUrl;
		this.apiBaseUrl = apiBaseUrl || walletScannerUrl || DEFAULT_API_BASE_URL;
		this.walletScannerBaseUrl = walletScannerUrl || this.apiBaseUrl;
		this.apiKey = normaliseApiKey(apiKey || DEFAULT_ZCASH_API_KEY);
		this.webzjsClient = getWebZjsClient(network, lightwalletdUrl);
		this.walletScanner = createWalletScannerClient({ 
			baseUrl: this.walletScannerBaseUrl,
			apiKey: this.apiKey
		});
	}

	setApiKey(apiKey?: string): void {
		this.apiKey = normaliseApiKey(apiKey);
		this.walletScanner = createWalletScannerClient({
			baseUrl: this.walletScannerBaseUrl,
			apiKey: this.apiKey
		});
	}

	private getApiHeaders(): Record<string, string> {
		const headers: Record<string, string> = {
			'Content-Type': 'application/json'
		};
		if (this.apiKey) {
			headers['X-API-Key'] = this.apiKey;
		}
		return headers;
	}

	private getScannerAuth(): { apiKey?: string } | undefined {
		return this.apiKey ? { apiKey: this.apiKey } : undefined;
	}

	/**
	 * Initialize the client
	 */
	async initialize(): Promise<void> {
		try {
			// Install fetch interceptor for WebZjs WASM gRPC-web compatibility
			installFetchInterceptor();
			
			await this.webzjsClient.initialize();
			console.log('✅ Shielded transaction client initialized');
		} catch (error) {
			console.warn('⚠️ WebZjs initialization failed (may not be needed for balance checks):', error.message);
			// Don't throw - we can still do balance checks via wallet scanner API
		}
	}

	private async exportUfvkLocal(mnemonic: string, accountIndex: number = 0): Promise<string> {
		const normalised = normaliseMnemonic(mnemonic);
		if (!validateMnemonic(normalised, wordlist)) {
			throw new Error('Invalid mnemonic phrase provided');
		}
		const ufvk = await this.webzjsClient.exportUnifiedFullViewingKey(normalised, { account: accountIndex });
		if (!ufvk) {
			throw new Error('UFVK export failed');
		}
		return ufvk;
	}

	/**
	 * Get complete balance breakdown for a unified address
	 * Uses wallet scanner for shielded pools, RPC for transparent
	 * 
	 * @param onProgress - Optional callback for progress updates (receives partial balance + progress info)
	 */
	async getShieldedBalance(
		mnemonic: string,
		unifiedAddress: string,
		transparentAddress?: string,
		accountIndex: number = 0,
		birthdayHeight?: number,
		onProgress?: (balance: ShieldedBalance) => void,
		autoDetectBirthday: boolean = true,
		cancelSignal?: AbortSignal,
		forceRescan: boolean = false,
		preExportedUfvk?: string,
		preferFreshScan: boolean = false
	): Promise<ShieldedBalance> {
		console.log('💰 Getting complete shielded balance breakdown...');
		console.log(`   Unified: ${unifiedAddress.substring(0, 20)}...`);
		if (transparentAddress) {
			console.log(`   Transparent: ${transparentAddress}`);
		}
		if (forceRescan) {
			console.log('🔄 Force-rescan enabled: bypassing cached note snapshots');
		}
		if (preferFreshScan) {
			console.log('🔄 Fresh-scan hint enabled: checking scanner for newly arrived notes');
		}

		try {
			// 1. Get transparent balance via RPC
			if (cancelSignal?.aborted) {
				throw new Error('Balance scan cancelled');
			}
			let transparentBalance = 0n;
			if (transparentAddress) {
				try {
					console.log('🔓 Checking transparent balance via RPC...');
					const rpcResponse = await fetchWithTimeout(RPC_ENDPOINT, {
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({
							jsonrpc: "1.0",
							id: "transparent-balance",
							method: "getaddressbalance",
							params: [{ "addresses": [transparentAddress] }]
						})
					}, RPC_FETCH_TIMEOUT_MS);

				const rpcData = await rpcResponse.json();
				if (rpcData.error) {
					console.warn('⚠️ RPC returned error for transparent balance:', rpcData.error);
				} else if (rpcData.result && typeof rpcData.result.balance === 'number') {
						transparentBalance = BigInt(rpcData.result.balance);
						console.log(`✅ Transparent: ${transparentBalance} zatoshis`);
					}
				} catch (error) {
					console.warn('⚠️ Transparent balance check failed (non-fatal):', error);
				}
			}

			// 2. Get shielded balances via wallet scanner
			let saplingBalance = 0n;
			let orchardBalance = 0n;
			let ironwoodBalance = 0n;

			try {
				console.log('🌿🌸 Checking shielded balances via wallet scanner...');
				if (cancelSignal?.aborted) {
					throw new Error('Balance scan cancelled');
				}
				
		let ufvk = '';
		if (preExportedUfvk) {
			ufvk = preExportedUfvk;
			console.log(`🔑 Using pre-exported UFVK: ${ufvk.substring(0, 30)}...`);
		} else {
			try {
				console.log('🔑 Exporting UFVK locally (mnemonic stays in browser)...');
				ufvk = await this.exportUfvkLocal(mnemonic, accountIndex);
				console.log(`🔑 Exported UFVK (account-level): ${ufvk.substring(0, 30)}...`);
			} catch (error) {
				console.warn('⚠️ UFVK export failed locally:', error);
				throw new Error('UFVK export failed - local derivation unavailable');
			}
		}

			// Use cached notes if fresh and chain is still at/behind cached height.
			let latestHeightForScan: number | null = null;
			if (ufvk) {
				const cacheKey = ufvk;
				const cached = noteCache.get(cacheKey);
				const nowTs = Date.now();
				const cachedNotes = Array.isArray(cached?.notes) ? cached.notes : [];
				const cachedHasNotes = cachedNotes.length > 0;
				const cacheTtlMs = cachedHasNotes ? NOTE_CACHE_TTL : EMPTY_NOTE_CACHE_TTL;
				const cachedHasWitness = cachedHasNotes ? cached?.hasWitnessData : true;
				const cacheLatestHeight = normaliseBlockHeight(cached?.latestHeight);
				latestHeightForScan = await fetchLatestBlockHeight();
				const cacheIsChainFresh = latestHeightForScan === null
					? true
					: (cacheLatestHeight === null || latestHeightForScan <= cacheLatestHeight + CACHE_CHAIN_STALENESS_BLOCKS);

				// For balance display we don't require witness data — only
				// sendShieldedTransaction needs it.  Accepting witness-less
				// cached notes here avoids a full rescan on every page reload.
				if (!forceRescan && !preferFreshScan && cached
					&& (nowTs - cached.timestamp < cacheTtlMs)
					&& (!birthdayHeight || cached.birthdayHeight <= birthdayHeight)
					&& cacheIsChainFresh
				) {
					const cachedPools = sumNotesByPool(cachedNotes);
					const totalCached = transparentBalance + cachedPools.orchard + cachedPools.ironwood;
					console.log('💾 Using cached scan results (no scan required)');
					const cachedBalance: ShieldedBalance = {
						transparent: transparentBalance,
						sapling: 0n,
						orchard: cachedPools.orchard,
						ironwood: cachedPools.ironwood,
						total: totalCached,
						formatted: {
							transparent: this.zatoshisToZecString(transparentBalance),
							sapling: '0.00000000',
							orchard: this.zatoshisToZecString(cachedPools.orchard),
							ironwood: this.zatoshisToZecString(cachedPools.ironwood),
							total: this.zatoshisToZecString(totalCached)
						},
						scanProgress: {
							currentBlock: 0,
							latestBlock: cacheLatestHeight || latestHeightForScan || 0,
							blocksScanned: 0,
							transactionsScanned: 0,
							notesFound: cachedNotes.length,
							percentComplete: 100,
							isScanning: false,
							jobId: undefined,
							birthdayHeight: cached.birthdayHeight,
							birthdayAutoDetected: false
						}
					};
					if (onProgress) {
						onProgress(cachedBalance);
					}
					return cachedBalance;
				}
			}

			// Scan for notes using wallet scanner (async chunked scan for balance check)
			console.log('📡 Starting chunked scan job...');
			
			// Use auto-detect if enabled and no birthday height provided
			const useAutoDetect = autoDetectBirthday && !birthdayHeight;
			if (forceRescan && latestHeightForScan === null) {
				latestHeightForScan = await fetchLatestBlockHeight();
			}
			
			if (useAutoDetect) {
				console.log('🔍 Auto-detecting birthday height (reverse scan from tip)...');
				console.log('⏱️  This may take 10-30 minutes for first scan, then cached');
			} else if (birthdayHeight) {
				console.log(`📅 Using specified birthday height: ${birthdayHeight}`);
			} else {
				console.log(`📅 Using default birthday height (NU6: 3042000)`);
			}
			
			const scanJobResponse = await this.walletScanner.startOrchardScanJob({
				ufvk,
				birthdayHeight: birthdayHeight || undefined,
				autoDetect: useAutoDetect,
				witness: true,
				forceRescan,
				endHeight: forceRescan ? (latestHeightForScan ?? undefined) : undefined
			});

			if (!scanJobResponse.success || !scanJobResponse.data) {
				throw new Error(`Failed to start scan job: ${scanJobResponse.error || 'unknown error'}`);
			}

			const { jobId, jobToken } = scanJobResponse.data;
			console.log(`📋 Scan job started: ${jobId}`);

			// Immediately return transparent balance if callback provided
			if (onProgress) {
				const initialBalance: ShieldedBalance = {
					transparent: transparentBalance,
					sapling: 0n,
					orchard: 0n,
					ironwood: 0n,
					total: transparentBalance,
					formatted: {
						transparent: this.zatoshisToZecString(transparentBalance),
						sapling: '0.00000000',
						orchard: '0.00000000',
						ironwood: '0.00000000',
						total: this.zatoshisToZecString(transparentBalance)
					},
					scanProgress: {
						currentBlock: 0,
						latestBlock: 0,
						blocksScanned: 0,
						transactionsScanned: 0,
						notesFound: 0,
						percentComplete: 0,
						isScanning: true,
						jobId
					}
				};
				onProgress(initialBalance);
			}

			// Poll for job completion with progress updates
			let attempts = 0;
			const maxAttempts = 240; // 20 minutes max (5 second intervals)
			let lastProgress = 0;
			let lastNotesFound = 0;
			let lastDetectFrom: number | null = null;
			// First scan position we observe = the real start of THIS scan.
			// Percentages are computed from position within that range —
			// dividing blocksScanned by an estimated range pinned the old
			// readout at 99.9% while it was still chewing through blocks.
			let scanRangeStart: number | null = null;

			while (attempts < maxAttempts) {
				if (cancelSignal?.aborted) {
					try {
						await this.walletScanner.cancelOrchardScanJob(jobId, { jobToken });
					} catch (cancelError) {
						console.warn('⚠️ Failed to cancel scan job:', cancelError);
					}
					throw new Error('Balance scan cancelled');
				}
				await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds

				const jobStatus = await this.walletScanner.getOrchardScanJob(jobId, { jobToken });

				if (!jobStatus.success || !jobStatus.data) {
					throw new Error('Failed to get job status');
				}

			const job = jobStatus.data.job;
			const jobAny = job as Record<string, any> | undefined;
			const progress = jobAny?.progress || {};
			const currentBlock = progress.currentStart || 0;
			// Two different heights, and conflating them was the progress bug:
			//   progress.latestHeight — coverage high-water mark (last block
			//     actually scanned). This is what the note cache must record.
			//   progress.chainTip     — the tip lightwalletd reported. This is
			//     what a progress bar has to divide by.
			// currentStart is always latestHeight + 1, so dividing by
			// latestHeight pinned the readout at 99.9% for the entire scan and
			// displayed the cursor as "Latest block".
			const latestBlockForCache = normaliseBlockHeight(progress.latestHeight);
			const scannedHeight = latestBlockForCache ?? 0;
			const chainTip = normaliseBlockHeight(progress.chainTip)
				?? normaliseBlockHeight(latestHeightForScan);
			const latestBlock = chainTip ?? 0;
			const blocksScanned = progress.blocksScanned || 0;
				const notesFound = progress.notesFound || 0;
				const transactionsScanned = progress.transactionsScanned || 0;
			const phase = typeof progress.phase === 'string' ? progress.phase : null;
			const isDetecting = phase === 'detecting-birthday';
			const detectFrom = normaliseBlockHeight(progress.detectFrom);
			const detectFloor = normaliseBlockHeight(progress.detectFloor);
			const jobParams = jobAny?.params || {};
			const birthdayHeightValue = typeof jobParams?.birthdayHeight === 'number'
				? jobParams.birthdayHeight
				: (typeof birthdayHeight === 'number' ? birthdayHeight : null);
			const birthdayAutoDetected = Boolean(jobParams?.birthdayHeightAutoDetected);
				
				// Check if birthday was auto-detected
			if (jobParams?.birthdayHeightAutoDetected && jobParams?.birthdayHeight) {
				console.log(`✨ Birthday height auto-detected: ${jobParams.birthdayHeight}`);
				}

				// Per-pool partial balances from the notes found so far.
				let partialOrchardBalance = 0n;
				let partialIronwoodBalance = 0n;
			const notes: any[] = Array.isArray(jobAny?.results?.notes) ? jobAny?.results?.notes : [];

			if (notes.length > 0) {
				const hasWitnessDuringScan = notes.every((note: any) => hasFullMerklePathWitness(note));
				if (hasWitnessDuringScan) {
					const cacheKey = ufvk;
					const existingCache = noteCache.get(cacheKey);
					// Only update cache during scan if we have MORE notes than
					// the existing cache — never reduce note count mid-scan.
					const existingCount = existingCache?.notes?.length ?? 0;
					const shouldUpdateCache = !existingCache
						|| !existingCache.hasWitnessData
						|| notes.length > existingCount;
					if (shouldUpdateCache) {
						const cachedNotes = notes.map((note: any) => ({ ...note }));
						const progressBirthdayHeight = birthdayHeight || jobParams?.birthdayHeight || 0;
						noteCache.set(cacheKey, {
							notes: cachedNotes,
							timestamp: Date.now(),
							ufvk,
							birthdayHeight: progressBirthdayHeight,
							hasWitnessData: true
						});
						_persistNoteCache();
						console.log(`💾 Cached ${notes.length} notes during scan progress (witness-ready, was ${existingCount})`);
					}
				}
			}
			{
				const partialPools = sumNotesByPool(notes);
				partialOrchardBalance = partialPools.orchard;
				partialIronwoodBalance = partialPools.ironwood;
			}

			// Show progress if changed significantly OR notes were found.
			// The detect clause matters: during the backwards walk currentStart
			// is null and notesFound stays 0, so none of the other conditions
			// ever fire and the client emitted nothing for the whole walk.
			const shouldUpdate = currentBlock > lastProgress + 500 ||
				job.status !== 'running' ||
				notesFound !== lastNotesFound ||
				(isDetecting && detectFrom !== null && detectFrom !== lastDetectFrom);
			
			if (shouldUpdate) {
				// Percent of THIS scan (birthday → tip), from the scanner's
				// position within the range — NOT blocksScanned / estimate,
				// which over-counted (chunk overlap, transparent passes) and
				// pinned the readout at 99.9% for entire scans.
				let percentDone: number | null = null;
				if (isDetecting) {
					// The walk has its own bar, running the other way.
					percentDone = computeWalkPercent(detectFrom, chainTip, detectFloor);
				} else if (!birthdayAutoDetected && latestBlock > currentBlock && currentBlock > 0) {
					if (scanRangeStart === null || currentBlock < scanRangeStart) {
						scanRangeStart = typeof birthdayHeightValue === 'number' && birthdayHeightValue > 0
							? Math.min(birthdayHeightValue, currentBlock)
							: currentBlock;
					}
					percentDone = computeScanPercent(currentBlock, scanRangeStart, chainTip);
				}
				const percentLabel = percentDone !== null ? ` (${percentDone}% of this scan)` : '';
				if (isDetecting) {
					console.log(
						`🔍 Searching backwards for the first transaction: at block ${detectFrom ?? '?'}` +
						` (tip ${chainTip ?? '?'}, floor ${detectFloor ?? '?'})${percentLabel}`
					);
				} else {
					console.log(`📊 Progress: ${blocksScanned} blocks, ${transactionsScanned} tx, ${notesFound} notes${percentLabel}`);
				}
				lastProgress = currentBlock;
				lastNotesFound = notesFound;
				lastDetectFrom = detectFrom;

				// Send progress update via callback
				if (onProgress) {
					const partialTotal = transparentBalance + partialOrchardBalance + partialIronwoodBalance;
					const progressUpdate: ShieldedBalance = {
						transparent: transparentBalance,
						sapling: 0n,
						orchard: partialOrchardBalance,
						ironwood: partialIronwoodBalance,
						total: partialTotal,
						formatted: {
							transparent: this.zatoshisToZecString(transparentBalance),
							sapling: '0.00000000',
							orchard: this.zatoshisToZecString(partialOrchardBalance),
							ironwood: this.zatoshisToZecString(partialIronwoodBalance),
							total: this.zatoshisToZecString(partialTotal)
						},
						scanProgress: {
							currentBlock,
							latestBlock,
							scannedHeight,
							phase,
							detectFrom,
							detectFloor,
							blocksScanned,
							transactionsScanned,
							notesFound,
							percentComplete: percentDone,
							isScanning: job.status === 'running',
							jobId,
							birthdayHeight: birthdayHeightValue,
							birthdayAutoDetected
						}
					};
					onProgress(progressUpdate);
				}
			}

			if (job.status === 'succeeded') {
				console.log(`✅ Scan complete: found ${notes.length} notes`);
				console.log(`📈 Stats: ${blocksScanned} blocks scanned, ${transactionsScanned} transactions checked`);

				// Use final balance from notes
				orchardBalance = partialOrchardBalance;
				ironwoodBalance = partialIronwoodBalance;

				// Cache scan results for future sends (reuse within TTL).
				// NEVER overwrite a cache that has notes with a SMALLER result
				// unless this was a forced full rescan — incremental/refresh scans
				// can legitimately return fewer notes (partial range, scanner lag)
				// and must not destroy previously found notes.
				if (ufvk) {
					const hasWitnessData = notes.length === 0 ? true : hasFullMerklePathWitness(notes[0]);
					const actualBirthdayHeight = birthdayHeight || jobParams?.birthdayHeight || 0;
					const cacheLatestHeight = latestBlockForCache ?? undefined;
					const cacheKey = ufvk;
					const existingCache = noteCache.get(cacheKey);
					const existingNoteCount = existingCache?.notes?.length ?? 0;
					const existingHasNotes = existingNoteCount > 0;
					const existingHasRho = existingHasNotes
						? existingCache!.notes.some((note: any) => Boolean(note?.rho || note?.rho_hex))
						: false;
					// Empty scan must not count as "has rho" — otherwise forceRescan
				// overwrites a good cache with an empty snapshot (random 0 balance).
				const scannedHasRho = notes.length > 0
						? notes.some((note: any) => Boolean(note?.rho || note?.rho_hex))
						: false;

				const allowForceUpdate = forceRescan && (scannedHasRho || !existingHasRho);
				if (allowForceUpdate || !existingHasNotes) {
					const cachedNotes = notes.map((note: any) => ({ ...note }));
					noteCache.set(cacheKey, {
						notes: cachedNotes,
						timestamp: Date.now(),
						ufvk,
						birthdayHeight: actualBirthdayHeight,
						hasWitnessData,
						latestHeight: cacheLatestHeight
					});
					_persistNoteCache();
					_dispatchBalanceEvent(ufvk);
					console.log(
						notes.length
							? `💾 Cached ${notes.length} notes for future sends (was ${existingNoteCount}, witness: ${hasWitnessData}, birthday: ${actualBirthdayHeight})`
							: `💾 Cached empty scan (was ${existingNoteCount}, birthday: ${actualBirthdayHeight})`
					);
				} else if (notes.length > 0 && existingHasNotes) {
					// Retire first, merge second. The scan is authoritative for the
					// range it covered, so anything it dropped from that range is
					// spent and must leave the cache — a union-only merge kept
					// spent notes and got the next send rejected as a double-spend.
					const { kept: survivingCached, retired } = retireNotesMissingFromScan(
						existingCache!.notes,
						notes,
						actualBirthdayHeight,
						cacheLatestHeight
					);
					const mergedMap = new Map<string, any>();
					for (const note of survivingCached) {
						const key = noteIdentityKey(note);
						mergedMap.set(key || `__pos_${note?.position ?? mergedMap.size}`, note);
					}
					let addedNew = false;
					for (const note of notes) {
						const identity = noteIdentityKey(note);
						const key = identity || `__pos_${note?.position ?? mergedMap.size}`;
						if (!mergedMap.has(key)) {
							addedNew = true;
						}
						if (identity && mergedMap.has(key)) {
							const existing = mergedMap.get(key);
							const existingHasRhoField = Boolean(existing?.rho || existing?.rho_hex);
							const newHasRhoField = Boolean(note?.rho || note?.rho_hex);
							if (newHasRhoField && !existingHasRhoField) {
								mergedMap.set(key, { ...note });
							}
						} else {
							mergedMap.set(key, { ...note });
						}
					}
					const mergedNotes = Array.from(mergedMap.values());
					noteCache.set(cacheKey, {
						notes: mergedNotes,
						timestamp: Date.now(),
						ufvk,
						birthdayHeight: actualBirthdayHeight,
						hasWitnessData: hasWitnessData || (existingCache?.hasWitnessData ?? false),
						latestHeight: cacheLatestHeight
					});
					({ orchard: orchardBalance, ironwood: ironwoodBalance } = sumNotesByPool(mergedNotes));
					_persistNoteCache();
					if (addedNew || retired > 0) _dispatchBalanceEvent(ufvk);
					console.log(
						`💾 Merged scan: ${notes.length} scanned + ${existingNoteCount} cached = ${mergedNotes.length} total` +
						(retired > 0 ? `, retired ${retired} spent` : '') +
						(addedNew ? ' (new notes found)' : ' (no new notes)')
					);
				} else if (notes.length > existingNoteCount) {
					const cachedNotes = notes.map((note: any) => ({ ...note }));
					noteCache.set(cacheKey, {
						notes: cachedNotes,
						timestamp: Date.now(),
						ufvk,
						birthdayHeight: actualBirthdayHeight,
						hasWitnessData,
						latestHeight: cacheLatestHeight
					});
					({ orchard: orchardBalance, ironwood: ironwoodBalance } = sumNotesByPool(cachedNotes));
					_persistNoteCache();
					_dispatchBalanceEvent(ufvk);
					console.log(`💾 Cached ${notes.length} notes (was ${existingNoteCount})`);
				} else {
					({ orchard: orchardBalance, ironwood: ironwoodBalance } = sumNotesByPool(existingCache!.notes));
					existingCache!.timestamp = Date.now();
					if (cacheLatestHeight !== undefined) {
						existingCache!.latestHeight = cacheLatestHeight;
					}
					_persistNoteCache();
					console.log(
						`⚠️ Scan returned ${notes.length} notes but cache has ${existingNoteCount} — ` +
						`keeping richer cache (${orchardBalance} zatoshis), refreshed timestamp`
					);
				}
				}

				console.log(`🌸 Orchard: ${orchardBalance} zatoshis`);
				console.log(`🪵 Ironwood: ${ironwoodBalance} zatoshis`);
				console.log(`🌿 Sapling: ${saplingBalance} zatoshis (not scanned)`);
				break;
			} else if (job.status === 'failed') {
				const jobError = jobAny?.error;
				const lower = String(jobError || '').toLowerCase();
				// The NFPT scanner surfaces lightwalletd connectivity
				// problems as a bare "transport error" / "scanner
				// error: transport error". Translate that into
				// something a user can act on instead of bubbling the
				// raw gRPC text up to the wallet UI.
				if (lower.includes('transport error') || lower.includes('connection refused')
					|| lower.includes('unreachable') || lower.includes('grpc')) {
					throw new Error(
						'Scanner backend cannot reach lightwalletd right now. ' +
						'This is usually transient — try again in a minute. ' +
						'If it persists, the scanner / lightwalletd service may be down.',
					);
				}
				throw new Error(`Scan job failed: ${jobError || 'unknown error'}`);
			} else if (job.status === 'cancelled') {
				throw new Error('Scan job was cancelled');
			}

				attempts++;
			}

			if (attempts >= maxAttempts) {
				console.error('❌ Scan job timed out after 20 minutes');
				throw new Error('Scan job timed out - wallet may have too many transactions');
			}
			} catch (error) {
				if (String(error?.message || '').toLowerCase().includes('cancelled')) {
					throw error;
				}
				console.warn('⚠️ Shielded balance scan failed:', error);
				const errMsg = String((error as Error)?.message || error);
				throw new Error(`Shielded scan failed: ${errMsg}`);
			}

			const total = transparentBalance + saplingBalance + orchardBalance + ironwoodBalance;

			const balance: ShieldedBalance = {
				transparent: transparentBalance,
				sapling: saplingBalance,
				orchard: orchardBalance,
				ironwood: ironwoodBalance,
				total,
				formatted: {
					transparent: this.zatoshisToZecString(transparentBalance),
					sapling: this.zatoshisToZecString(saplingBalance),
					orchard: this.zatoshisToZecString(orchardBalance),
					ironwood: this.zatoshisToZecString(ironwoodBalance),
					total: this.zatoshisToZecString(total)
				},
				scanProgress: {
					currentBlock: 0,
					latestBlock: 0,
					blocksScanned: 0,
					transactionsScanned: 0,
					notesFound: 0,
					percentComplete: 100,
					isScanning: false
				}
			};

			console.log('💎 Complete balance breakdown:');
			console.log(`   🔓 Transparent: ${balance.formatted.transparent} ZEC`);
			console.log(`   🌿 Sapling:     ${balance.formatted.sapling} ZEC`);
			console.log(`   🌸 Orchard:     ${balance.formatted.orchard} ZEC`);
			console.log(`   🪵 Ironwood:    ${balance.formatted.ironwood} ZEC`);
			console.log(`   💰 Total:       ${balance.formatted.total} ZEC`);

			return balance;

		} catch (error) {
			console.error('❌ Balance check failed:', error);
			throw new Error(`Failed to get shielded balance: ${error.message}`);
		}
	}

	/**
	 * Send a shielded transaction using wallet scanner witness data
	 * This is the CORRECT way to do shielded sends with proper witness data
	 */
	async sendShieldedTransaction(options: ShieldedTransactionOptions): Promise<string> {
		const {
			mnemonic,
			seedBytes,
			preExportedUfvk,
			transparentPrivateKeyHex,
			transparentSignFunction,
			transparentPublicKeyHex,
			orchardFrostSignFunction,
			orchardFrostGroupPublicHex,
			saplingFrostSignFunction,
			fromAddress,
			transparentAddress,
			toAddress,
			amountZec: amountZecParam,
			sendAll = false,
			memo,
			accountIndex = 0,
			birthdayHeight,
			autoDetectBirthday = true,
			allowTransparentInputs = false,
			skipShieldedScan = false,
			shieldedInputPoolPreference = 'auto',
			commissionBps = DEFAULT_SHIELDED_COMMISSION_BPS,
			commissionUnifiedAddress = DEFAULT_SHIELDED_COMMISSION_UA,
			onStatus,
			onFeeWarning,
			abortSignal,
		} = options;

		// NU6.3 / Ironwood made every Orchard proof this wallet can build invalid.
		// Refuse before scanning or signing rather than failing at broadcast.
		// A transparent-only send touches no shielded pool, so it still runs.
		assertShieldedSendAvailable(skipShieldedScan && allowTransparentInputs);

		const throwIfCancelled = () => {
			if (abortSignal?.aborted) {
				const err: any = new Error('Shielded send cancelled by user.');
				err.name = 'AbortError';
				err.code = 'USER_CANCELLED';
				throw err;
			}
		};
		const hasMnemonic = !!mnemonic;
		const hasSeedBytes = seedBytes && seedBytes.length >= 32;
		const hasTransparentKey = !!transparentPrivateKeyHex && /^[0-9a-fA-F]{64}$/.test(transparentPrivateKeyHex);
		const hasSignFunction = typeof transparentSignFunction === 'function';

		// The server needs the transparent public key at *build* time, not just at
		// signing time: since NU6.3 its builder verifies hash160(pubkey) against
		// each input's scriptPubKey, and cannot recover the key from the script.
		// Resolved lazily, because a shielded-only send has no transparent input
		// to spend and should not care whether a transparent key exists.
		const resolveTransparentPubkeyHex = (): string | undefined => {
			if (transparentPublicKeyHex && /^[0-9a-fA-F]{66}$/.test(transparentPublicKeyHex)) {
				return transparentPublicKeyHex.toLowerCase();
			}
			try {
				if (hasMnemonic) return deriveTransparentKeyPair(mnemonic).publicKeyHex;
				if (hasTransparentKey) {
					return bytesToHex(secp256k1.getPublicKey(hexToBytes(transparentPrivateKeyHex!), true));
				}
			} catch (error) {
				console.warn('⚠️ Could not derive the transparent public key:', error);
			}
			return undefined;
		};
		const useFrostOrchard = typeof orchardFrostSignFunction === 'function';
		const useFrostSapling = typeof saplingFrostSignFunction === 'function';

		const emitStatus = (message: string) => {
			if (typeof onStatus !== 'function') return;
			try {
				onStatus(message);
			} catch (error) {
				console.warn('⚠️ Status callback failed:', error);
			}
		};

		let amountZec = amountZecParam;
		let amountZatoshis = sendAll ? 0 : Math.floor(amountZec * ZATOSHIS_PER_ZEC);

		if (!hasMnemonic && !hasSeedBytes && !preExportedUfvk) {
			throw new Error('A mnemonic, seed bytes, or pre-exported UFVK is required for shielded sends.');
		}
		if (!sendAll && (!Number.isFinite(amountZec) || amountZec <= 0 || amountZatoshis <= 0)) {
			throw new Error('Shielded send amount must be greater than zero.');
		}
		if (typeof fromAddress !== 'string' || !fromAddress.startsWith('u')) {
			throw new Error('Shielded send source must be a unified address.');
		}
		if (typeof toAddress !== 'string' || toAddress.trim().length === 0) {
			throw new Error('Destination address is required.');
		}

		console.log('🚀 Starting shielded transaction with server-side PCZT building...');
		console.log(`   From: ${fromAddress.substring(0, 20)}...`);
		console.log(`   To: ${toAddress.substring(0, 20)}...`);
		console.log(`   Amount: ${sendAll ? 'MAX (all available)' : amountZec + ' ZEC'}`);
		emitStatus('Preparing shielded transaction...');

		let cacheKey = '';
		let ufvk = '';
		let pendingNullifiersForAttempt: string[] = [];
		let latestBlockHeight: number | null = null;
		let transparentFallbackEnabled = false;
		let skipShieldedScanRequested = false;

		try {
			await this.webzjsClient.initialize();

			// 1. Export UFVK locally (secrets stay in browser)
			emitStatus('Exporting UFVK...');
			console.log('Step 1: Exporting UFVK locally...');
			if (preExportedUfvk) {
				ufvk = preExportedUfvk;
			} else if (hasSeedBytes) {
				const result = await this.webzjsClient.generateUnifiedAddressFromSeed(seedBytes!, accountIndex);
				ufvk = result.ufvk;
			} else {
				ufvk = await this.exportUfvkLocal(mnemonic, accountIndex);
			}
			cacheKey = ufvk;
			console.log(`✅ UFVK (account-level): ${ufvk.substring(0, 30)}...`);
			if (skipShieldedScan) {
				emitStatus('UFVK exported. Skipping shielded scan...');
			} else {
				emitStatus('UFVK exported. Scanning for spendable notes...');
			}

		const commissionRateBps = Number.isFinite(Number(commissionBps))
			? Math.max(0, Math.floor(Number(commissionBps)))
			: 0;
		const commissionRecipient = String(commissionUnifiedAddress || '').trim();
		const commissionEnabled =
			commissionRateBps > 0 &&
			commissionRecipient.startsWith('u1') &&
			commissionRecipient !== toAddress;
		let commissionZatoshis = commissionEnabled
			? Math.max(1, Math.floor((amountZatoshis * commissionRateBps) / 10000))
			: 0;

		if (commissionRateBps > 0 && !commissionEnabled) {
			emitStatus('Shielded commission is configured but disabled (invalid recipient or duplicate destination).');
		}
		if (commissionEnabled) {
			emitStatus(
				`Applying shielded commission: ${(commissionRateBps / 100).toFixed(2)}% (${(commissionZatoshis / ZATOSHIS_PER_ZEC).toFixed(8)} ZEC).`
			);
		}
		// Start with the minimum ZIP-317 fee (2 grace actions × 5000 = 10 000).
		// This is recalculated to the exact amount once we know the transaction shape.
		let feeZatoshis = DEFAULT_SHIELDED_FEE_ZATOSHIS;
		let requiredZatoshis = amountZatoshis + feeZatoshis + commissionZatoshis;
		let requiredZatoshisBig = BigInt(requiredZatoshis);
		skipShieldedScanRequested = skipShieldedScan === true;
		// When skip-scan is requested, transparentFallback is implicitly required
		transparentFallbackEnabled = allowTransparentInputs === true || skipShieldedScanRequested;
		if (transparentFallbackEnabled && !transparentAddress) {
			throw new Error('Transparent address is required to shield transparent funds.');
		}
		if (skipShieldedScanRequested) {
			emitStatus('Skipping shielded scan. Using transparent inputs only.');
			console.log('⏩ skipShieldedScan=true — will skip note scan entirely and use transparent UTXOs');
		}

		latestBlockHeight = await fetchLatestBlockHeight();
		if (latestBlockHeight !== null) {
			console.log(`⛓️ Latest block height: ${latestBlockHeight}`);
		}

		// Check note cache first
		// Use UFVK as primary cache key (notes are the same for a given UFVK)
		let cached = noteCache.get(cacheKey);
		const now = Date.now();
		const cacheLatestHeight = normaliseBlockHeight(cached?.latestHeight);
		const cacheIsChainFresh = latestBlockHeight === null
			? true
			: (cacheLatestHeight === null || latestBlockHeight <= cacheLatestHeight + CACHE_CHAIN_STALENESS_BLOCKS);
		const cachedNotes = Array.isArray(cached?.notes) ? cached.notes : [];
		const cachedHasNotes = cachedNotes.length > 0;
		const cacheTtlMs = cachedHasNotes ? NOTE_CACHE_TTL : EMPTY_NOTE_CACHE_TTL;
		const cachedHasRho = cachedHasNotes
			? cachedNotes.some((note: any) => Boolean(note?.rho || note?.rho_hex))
			: true;
		if (cached && cachedHasNotes && !cachedHasRho) {
			_deleteCacheEntry(cacheKey);
			_persistNoteCache();
			clearNoteCacheForAddress(fromAddress);
			cached = null;
			console.log('🗑️ Cleared cached notes missing rho; will rely on server cache');
		}
		
		let notes: any[] = [];
		let useTransparentInputs = false;
		const pendingNullifiers = ufvk ? getPendingNullifiersForUfvk(ufvk) : new Set<string>();
		
		// Check if cached notes are usable:
		// 1. Cache is fresh (within TTL)
		// 2. Cached notes have witness data (required for sending)
		// 3. Requested birthday height is >= cached birthday height (notes are still valid)
		const cachedHasWitness = cachedHasNotes ? cached?.hasWitnessData : true;
		const canUseCache = cached && 
			(now - cached.timestamp < cacheTtlMs) && 
			cachedHasWitness && // Must have witness data for sending
			(!birthdayHeight || cached.birthdayHeight <= birthdayHeight) && // Cache covers requested range
			cacheIsChainFresh &&
			cachedHasRho;
		
		if (skipShieldedScanRequested) {
			// Hard guard: never scan or use cached notes when skipping is requested.
			notes = [];
		} else {
			if (canUseCache) {
				// Use cached notes if fresh and valid
				const ageSeconds = Math.floor((now - cached.timestamp) / 1000);
				const ageMinutes = Math.floor(ageSeconds / 60);
				const cacheLabel = cachedHasNotes ? `${cachedNotes.length} notes` : 'no notes';
				console.log(`💾 Using cached scan (${cacheLabel}, ${ageMinutes}m ${ageSeconds % 60}s old)`);
				console.log(
					`   Cached from birthday: ${cached.birthdayHeight}, has witness: ${cachedHasWitness}, ` +
					`latest: ${cacheLatestHeight ?? 'unknown'}`
				);
				notes = cachedNotes;
				emitStatus(cachedHasNotes ? `Using cached notes (${cachedNotes.length})...` : 'Using cached empty scan...');
			} else {
				if (cached) {
					console.log(
						`⚠️ Cache exists but unusable: age=${Math.floor((now - cached.timestamp) / 1000)}s, ` +
						`witness=${cached.hasWitnessData}, birthday=${cached.birthdayHeight} vs requested ${birthdayHeight || 'auto'}, ` +
						`chainFresh=${cacheIsChainFresh}, rho=${cachedHasRho}, cacheTip=${cacheLatestHeight ?? 'unknown'}, ` +
						`latest=${latestBlockHeight ?? 'unknown'}`
					);
				}
				const useAutoDetect = autoDetectBirthday && !birthdayHeight;
				const runScanJob = async (forceRescan: boolean) => {
					// 2. Start wallet scanner job with witness data
					emitStatus(forceRescan ? 'Rescanning notes with witness data...' : 'Starting witness scan...');
					console.log('Step 2: Starting wallet scanner job (witness=true)...');
					if (useAutoDetect) {
						console.log('🔍 Auto-detecting birthday height for transaction...');
					}
					if (forceRescan) {
						console.log('🔄 Forcing rescan to refresh rho data...');
					}

					const scanJobResult = await this.walletScanner.startOrchardScanJob(
						{
							ufvk,
							birthdayHeight: birthdayHeight || undefined,
							autoDetect: useAutoDetect,
							witness: true, // CRITICAL: Get witness data for spending
							forceRescan,
							endHeight: forceRescan ? (latestBlockHeight ?? undefined) : undefined
						},
						this.getScannerAuth()
					);

					if (!scanJobResult.success || !scanJobResult.data) {
						throw new Error('Failed to start wallet scanner job');
					}

					const { jobId, jobToken } = scanJobResult.data;
					console.log(`✅ Scan job started: ${jobId}`);

					// 3. Poll for job completion (or early exit if we have enough)
					console.log('Step 3: Waiting for witness scan to complete...');
					console.log(`💰 Need ${requiredZatoshis / 100000000} ZEC (amount + fee) to proceed with send`);

					let jobStatus: any;
					let pollCount = 0;
					let lastProgressNotice = '';
					// The scan job has no server-side percentComplete, so derive
					// one the same way the balance poller does: cursor position
					// between the range start and the chain tip.
					let sendScanRangeStart: number | null = null;
					const maxPolls = 240; // 20 minutes max (5 second intervals)
					const pollIntervalMs = 5000;
					const pollIntervalSeconds = Math.max(1, Math.floor(pollIntervalMs / 1000));

					while (pollCount < maxPolls) {
						// Respect user cancellation: waking every pollIntervalMs is
						// enough — we don't need AbortSignal-aware sleep because the
						// user can already see a spinner and any Cancel click will
						// show up on the next tick.
						await new Promise(resolve => setTimeout(resolve, pollIntervalMs));
						throwIfCancelled();

						const statusResult = await this.walletScanner.getOrchardScanJob(jobId, { jobToken });

						if (!statusResult.success) {
							throw new Error('Failed to get scan job status');
						}

						jobStatus = statusResult.data?.job;
						const status = jobStatus?.status;
						const sendProgress = jobStatus?.progress || {};
						const sendCursor = Number(sendProgress.currentStart) || 0;
						const sendTip = normaliseBlockHeight(sendProgress.chainTip)
							?? normaliseBlockHeight(latestBlockHeight);
						const sendAutoDetected = Boolean(jobStatus?.params?.birthdayHeightAutoDetected);
						const sendPhase = typeof sendProgress.phase === 'string' ? sendProgress.phase : null;
						const sendDetecting = sendPhase === 'detecting-birthday';
						let progressPercent: number | null = null;
						if (sendDetecting) {
							// Backwards walk: same dead air as the balance scan had.
							progressPercent = computeWalkPercent(
								normaliseBlockHeight(sendProgress.detectFrom),
								sendTip,
								normaliseBlockHeight(sendProgress.detectFloor)
							);
						} else if (!sendAutoDetected && sendTip !== null && sendTip > sendCursor && sendCursor > 0) {
							if (sendScanRangeStart === null || sendCursor < sendScanRangeStart) {
								sendScanRangeStart = sendCursor;
							}
							progressPercent = computeScanPercent(sendCursor, sendScanRangeStart, sendTip);
						}

						if (status === 'succeeded') {
							const elapsedSeconds = (pollCount + 1) * pollIntervalSeconds;
							console.log(`✅ Witness scan completed after ${elapsedSeconds} seconds`);
							break;
						} else if (status === 'failed') {
							throw new Error(`Wallet scanner job failed: ${jobStatus?.error || 'Unknown error'}`);
						}

						pollCount++;
						if (pollCount % 4 === 0) {
							const interimTotal = (jobStatus?.results?.notes || []).reduce((sum: number, note: any) => sum + (note.value || 0), 0);
							const elapsedSeconds = pollCount * pollIntervalSeconds;
							console.log(`⏳ Still scanning... (${elapsedSeconds}s elapsed, found ${interimTotal / 100000000} ZEC so far)`);
							const activity = sendDetecting
								? 'Searching backwards for your first transaction'
								: 'Scanning notes';
							const progressLabel = progressPercent !== null
								? `${activity}... ${progressPercent.toFixed(1)}%`
								: `${activity}... ${elapsedSeconds}s elapsed`;
							if (progressLabel !== lastProgressNotice) {
								emitStatus(progressLabel);
								lastProgressNotice = progressLabel;
							}
						}
					}

					if (jobStatus?.status !== 'succeeded') {
						throw new Error('Wallet scanner job timed out');
					}

					return jobStatus;
				};

				let jobStatus = await runScanJob(false);
				notes = jobStatus?.results?.notes || [];
				const notesHaveRho = notes.some((note: any) => Boolean(note?.rho || note?.rho_hex));
				if (!notesHaveRho) {
					console.log('⚠️ Scan results missing rho; retrying with forced rescan...');
					jobStatus = await runScanJob(true);
					notes = jobStatus?.results?.notes || [];
				}

				console.log(`✅ Found ${notes.length} spendable notes with witness data`);
				emitStatus('Scan complete. Selecting notes...');

				const jobParams = (jobStatus as Record<string, any> | undefined)?.params || {};
				const actualBirthdayHeight = birthdayHeight || jobParams?.birthdayHeight || 0;
				const scanLatestHeight = (jobStatus as Record<string, any> | undefined)?.progress?.latestHeight;

				// Cache the scan result — same guard as getShieldedBalance:
				// never overwrite notes with empty unless forced.
				const hasWitnessData = notes.length === 0 ? true : hasFullMerklePathWitness(notes[0]);
				const scanCacheHeight = normaliseBlockHeight(scanLatestHeight)
					?? (normaliseBlockHeight(latestBlockHeight) ?? undefined);
				const existingSendCache = noteCache.get(cacheKey);
				const existingSendHasNotes = existingSendCache && Array.isArray(existingSendCache.notes) && existingSendCache.notes.length > 0;

				const existingSendHasRho = existingSendHasNotes
					? existingSendCache!.notes.some((note: any) => Boolean(note?.rho || note?.rho_hex))
					: false;
				const scannedSendHasRho = notes.length > 0
					? notes.some((note: any) => Boolean(note?.rho || note?.rho_hex))
					: true;
				// A fresh scan outranks the cache for the range it covered: a note
				// it no longer reports has been spent. Preferring the larger
				// cached set (what this used to do) is how an already-spent note
				// reached the PCZT builder and the broadcast came back
				// "duplicate nullifier ... in finalized state: true".
				const { kept: survivingCached, retired: retiredSendNotes } = retireNotesMissingFromScan(
					existingSendHasNotes ? existingSendCache!.notes : [],
					notes,
					actualBirthdayHeight,
					scanCacheHeight ?? null
				);
				if (retiredSendNotes > 0) {
					console.log(`🧹 Dropped ${retiredSendNotes} cached note(s) the scan reports as spent`);
				}

				if (existingSendHasRho && !scannedSendHasRho && survivingCached.length > 0) {
					// Scanner came back without rho — its notes can't be spent, so
					// fall back to the (now pruned) cached set rather than fail.
					notes = survivingCached.map((note: any) => ({ ...note }));
					console.log(
						`⚠️ Scan returned notes without rho while cache has rho-ready notes (${survivingCached.length}); ` +
						`using cached notes for send`
					);
					emitStatus(`Using ${survivingCached.length} cached note(s)...`);
				} else if (survivingCached.length > 0) {
					// Keep cached notes the scan did not cover (a shallower or
					// narrower scan must not lose them), scan result wins otherwise.
					const mergedMap = new Map<string, any>();
					for (const note of notes) {
						const key = noteIdentityKey(note);
						mergedMap.set(key || `__pos_${note?.position ?? mergedMap.size}`, { ...note });
					}
					let carried = 0;
					for (const note of survivingCached) {
						const key = noteIdentityKey(note) || `__pos_${note?.position ?? mergedMap.size}`;
						if (!mergedMap.has(key)) {
							mergedMap.set(key, { ...note });
							carried++;
						}
					}
					if (carried > 0) {
						console.log(`💾 Carried ${carried} cached note(s) from outside the scanned range`);
					}
					notes = Array.from(mergedMap.values());
				}

				if (notes.length === 0) {
					if (!transparentFallbackEnabled) {
						throw new Error('No spendable shielded notes found. Unified address may have no shielded funds.');
					}
					emitStatus('No shielded notes found. Attempting to use transparent funds...');
				}

				const keepsRhoQuality = scannedSendHasRho || !existingSendHasRho;
				// An empty result still must not flatten a populated cache — that
				// was the "random 0 balance" failure. Anything else is written,
				// including a set smaller than the cache: shrinking is the point.
				if ((notes.length > 0 && keepsRhoQuality) || !existingSendHasNotes) {
					noteCache.set(cacheKey, {
						notes: notes.map((note: any) => ({ ...note })),
						timestamp: Date.now(),
						ufvk,
						birthdayHeight: actualBirthdayHeight,
						hasWitnessData,
						latestHeight: scanCacheHeight
					});
					_persistNoteCache();
				}
				console.log(
					notes.length
						? `💾 Cached ${notes.length} notes for reuse (witness: ${hasWitnessData}, birthday: ${actualBirthdayHeight}, ` +
							`latest: ${typeof scanCacheHeight === 'number' ? scanCacheHeight : 'unknown'})`
						: `💾 Cached empty scan (birthday: ${actualBirthdayHeight}, latest: ${typeof scanCacheHeight === 'number' ? scanCacheHeight : 'unknown'})`
				);
			}
		}
		if (notes.length > 0 && pendingNullifiers.size > 0) {
			const beforeCount = notes.length;
			notes = notes.filter((note: any) => {
				const nf = normaliseNullifierHex(this.getNoteNullifierHex(note));
				return !(nf && pendingNullifiers.has(nf));
			});
			const filteredCount = beforeCount - notes.length;
			if (filteredCount > 0) {
				console.log(`⏳ Skipped ${filteredCount} pending note(s) already spent in mempool`);
			}
		}

		if (notes.length === 0 && pendingNullifiers.size > 0 && !transparentFallbackEnabled) {
			throw new Error(
				'All spendable notes are pending confirmation from a previous shielded send. Wait for confirmation and rescan.'
			);
		}

		if (notes.length > 0) {
			const notesBeforeRhoFilter = notes.length;
			notes = notes.filter((note: any) => {
				const noteNullifier = this.getNoteNullifierHex(note);
				const rhoHex = this.getNoteRhoHex(note);
				return Boolean(noteNullifier) && Boolean(rhoHex);
			});
			const rhoFilteredCount = notesBeforeRhoFilter - notes.length;
			if (rhoFilteredCount > 0) {
				console.log(`⚠️ Skipped ${rhoFilteredCount} note(s) missing rho or note nullifier`);
			}
		}

		const poolPref: ShieldedPool | null = shieldedInputPoolPreference === 'sapling'
			|| shieldedInputPoolPreference === 'orchard'
			|| shieldedInputPoolPreference === 'ironwood'
			? shieldedInputPoolPreference
			: null;
		if (poolPref && notes.length > 0) {
			const beforePool = notes.length;
			notes = notes.filter((note: any) => normaliseNotePool(note?.pool) === poolPref);
			if (beforePool > 0 && notes.length === 0) {
				throw new Error(
					`No spendable ${poolPref} notes found. Set shielded input pool to Auto in Advanced, ` +
					'or use the pool that actually holds your funds.'
				);
			}
		}

		if (notes.length === 0 && !transparentFallbackEnabled) {
			throw new Error('No spendable notes with rho available. Rescan with updated scanner and retry.');
		}

		// Log total available balance
		const totalAvailable = notes.reduce((sum: bigint, note: any) => sum + BigInt(note?.value || 0), 0n);
		const totalAvailableZec = Number(totalAvailable) / ZATOSHIS_PER_ZEC;
		console.log(`💰 Total available in notes: ${totalAvailableZec} ZEC`);

		const canSignTransparentInputs = hasMnemonic || hasTransparentKey || hasSignFunction;
		const shieldedInsufficient = notes.length === 0 || totalAvailable < requiredZatoshisBig;
		useTransparentInputs = transparentFallbackEnabled && canSignTransparentInputs && shieldedInsufficient;

		if (transparentFallbackEnabled && shieldedInsufficient && !canSignTransparentInputs) {
			const hint = notes.length === 0
				? 'No shielded notes found and transparent fallback was requested,'
				: 'Shielded balance is insufficient and transparent fallback was requested,';
			throw new Error(
				`${hint} but no transparent signing capability is available ` +
				'(no recovery phrase, private key, or sign function was provided).'
			);
		}

		if (useTransparentInputs && notes.length > 0) {
			console.log('⚠️ Shielded notes are insufficient; attempting mixed funding (shielded + transparent)');

			// Keep as much shielded input as possible (same-anchor selection) and
			// only top up the remaining shortfall from transparent UTXOs.
			try {
				const partialSelection = this.selectNotesForSpend(notes, totalAvailable);
				notes = partialSelection.selectedNotes;
				if (partialSelection.anchorRoot) {
					console.log(`🔗 Partial shielded selection anchor: ${partialSelection.anchorRoot.substring(0, 16)}...`);
				}
			} catch {
				// If no shared-anchor set can include all notes, keep the largest note
				// so we still contribute some shielded input to the mixed transaction.
				const largest = notes
					.slice()
					.sort((a: any, b: any) => Number(b?.value || 0) - Number(a?.value || 0))[0];
				notes = largest ? [largest] : [];
			}
			const partialTotal = notes.reduce((sum: bigint, note: any) => sum + BigInt(note?.value || 0), 0n);
			console.log(`💸 Mixed-mode shielded contribution: ${Number(partialTotal) / ZATOSHIS_PER_ZEC} ZEC (${partialTotal} zatoshis)`);
		}

		if (!useTransparentInputs) {
			// Select notes that share the same anchor root to avoid AnchorMismatch.
			// ZIP-317 fixed point: the fee depends on how many notes we spend and
			// how many notes we need depends on the fee, so iterate until stable —
			// the declared fee must always match the final transaction shape.
			const recipientIsUnified = toAddress.startsWith('u1');
			const plannedOrchardOutputs =
				(recipientIsUnified ? 1 : 0)
				+ (commissionZatoshis > 0 ? 1 : 0)
				// A change output, unless this is a sweep. Ordinarily the fee has
				// to be fixed before the change can be computed, so one is assumed
				// (mirroring the backend's conservative floor) — but a sweep sends
				// the inputs minus the fee and leaves nothing behind by
				// construction, so paying for an action that cannot exist is just
				// 5,000 zatoshis burnt on every pool migration.
				+ (sendAll ? 0 : 1);
			const plannedTransparentOutputs = recipientIsUnified ? 0 : 1;
			let selection = sendAll
				? this.selectAllNotesForSweep(notes)
				: this.selectNotesForSpend(notes, requiredZatoshisBig);
			if (!sendAll) {
				for (let iteration = 0; iteration < 6; iteration++) {
					const selectedPools = countSpendsByPool(selection.selectedNotes);
					const newFee = calculateZip317Fee({
						transparentInputs: 0,
						transparentOutputs: plannedTransparentOutputs,
						orchardSpends: selectedPools.orchard,
						ironwoodSpends: selectedPools.ironwood,
						orchardOutputs: plannedOrchardOutputs,
					});
					if (newFee === feeZatoshis) break;
					console.log(`💰 ZIP-317 fee (note selection): ${feeZatoshis} → ${newFee} zatoshis`);
					feeZatoshis = newFee;
					requiredZatoshis = amountZatoshis + feeZatoshis + commissionZatoshis;
					requiredZatoshisBig = BigInt(requiredZatoshis);
					selection = this.selectNotesForSpend(notes, requiredZatoshisBig);
				}
			}
			notes = selection.selectedNotes;
			if (selection.anchorRoot) {
				console.log(`🔗 Selected ${notes.length} note(s) with anchor ${selection.anchorRoot.substring(0, 16)}...`);
			} else {
				console.log('🔗 Selected single note without anchor root; using orchardTreeHex fallback');
			}
			const selectedTotal = notes.reduce((sum: bigint, note: any) => sum + BigInt(note?.value || 0), 0n);
			console.log(`💸 Selected notes total: ${Number(selectedTotal) / ZATOSHIS_PER_ZEC} ZEC (${selectedTotal} zatoshis)`);
		}

			// 5. Generate seed fingerprint and USK (must be from keys-and-send WASM)
			emitStatus('Deriving unified spending key...');
			console.log('Step 4: Creating seed fingerprint and unified spending key...');
			
			if (!hasSeedBytes && !hasMnemonic) {
				throw new Error(
					'Unable to derive spending key: wallet has no recovery phrase or seed bytes loaded. ' +
					'This can happen if the wallet state is still initialising — reload the wallet and retry.'
				);
			}
			const { usk, seedFp } = hasSeedBytes
				? await this.webzjsClient.generateUnifiedAddressFromSeed(seedBytes!, accountIndex)
				: await this.webzjsClient.generateUnifiedAddress(mnemonic, accountIndex);
			if (!seedFp || typeof (seedFp as { to_bytes?: () => Uint8Array }).to_bytes !== 'function') {
				throw new Error('Failed to derive seed fingerprint from WebZjs Keys-and-Send');
			}
			const seedFingerprintBytes = seedFp.to_bytes();
			const seedFingerprintHex = Buffer.from(seedFingerprintBytes).toString('hex');
			console.log(`   Seed fingerprint: ${seedFingerprintHex.substring(0, 16)}...`);
			
			const seedFingerprint = seedFp;

			// Amount already calculated above during polling
			console.log(`Step 5: Preparing to build PCZT for ${amountZatoshis} zatoshis (fee: ${feeZatoshis})...`);

			// 7. Build PCZT on server (Rust orchard-scanner)
			emitStatus('Building PCZT on server...');
			console.log('📝 Building PCZT on server...');
			
			let transparentInputsPayload: any[] = [];
			let transparentOutputsPayload: any[] = [];
			let changeUnifiedAddress: string | undefined;

			const isTransparentRecipient = toAddress.startsWith('t');
			const isUnifiedRecipient = toAddress.startsWith('u1');

			if (!isTransparentRecipient && !isUnifiedRecipient) {
				throw new Error('Recipient must be a unified (u1) or transparent (t) address');
			}

			if (memo && isTransparentRecipient) {
				throw new Error('Transparent recipients do not support memos');
			}

			const outputsPayload: any[] = [];
			let allNormalisedUtxos: any[] = [];  // populated when using transparent inputs; available for re-selection

			if (isUnifiedRecipient) {
				outputsPayload.push({
					toUnifiedAddress: toAddress,
					valueZatoshis: amountZatoshis,
					memoHex: memo ? Buffer.from(memo).toString('hex') : undefined
				});
			} else {
				transparentOutputsPayload.push({
					address: toAddress,
					valueZatoshis: amountZatoshis,
					userAddress: toAddress
				});
			}

			if (commissionZatoshis > 0) {
				outputsPayload.push({
					toUnifiedAddress: commissionRecipient,
					valueZatoshis: commissionZatoshis
				});
				console.log(
					`💸 Commission output added: ${(commissionZatoshis / ZATOSHIS_PER_ZEC).toFixed(8)} ZEC to ${commissionRecipient.substring(0, 20)}...`
				);
			}

			const spends = notes.map(note => {
					// Scanner now returns complete witness data!
					const leafPositionRaw =
						note.leaf_position ??
						note.leafPosition ??
						note.leafPos ??
						note.note_position ??
						note.position;
					const siblings = extractMerklePathSiblings(note);
					const anchorRoot = this.getAnchorRootHex(note);
					const hasWitnessData =
						Boolean(anchorRoot) &&
						leafPositionRaw !== undefined &&
						siblings.length === ORCHARD_MERKLE_DEPTH;

					const noteNullifierHex = this.getNoteNullifierHex(note);
					if (!noteNullifierHex) {
						throw new Error('Shielded note missing note nullifier. Rescan with witness=true and retry.');
					}
					const rhoHex = this.getNoteRhoHex(note);
					if (!rhoHex) {
						throw new Error('Shielded note missing rho. Rescan with updated scanner and retry.');
					}
					
					const spend: Record<string, any> = {
						// Which pool the note lives in decides the commitment tree
						// its witness is checked against and the note plaintext
						// version the server reconstructs it with. Nothing about
						// the note itself says which — Ironwood reuses the Orchard
						// address and keys — so it has to travel with the spend.
						pool: normaliseNotePool(note.pool),
						noteCommitmentHex: note.commitment,
						diversifierHex: note.note_plaintext?.diversifier || note.diversifier,
						pkdHex: note.note_plaintext?.pk_d || note.pk_d,
						rseedHex: note.note_plaintext?.rseed || note.rseed,
						rhoHex,
						valueZatoshis: note.value,
						anchorHeight: note.anchor_height,
						nullifierHex: noteNullifierHex,
						blockHeight: note.block_height
					};
					
					if (hasWitnessData) {
						// ✅ New format: Use unpacked witness data
						spend.anchorRootHex = anchorRoot;
						spend.leafPosition = String(leafPositionRaw);
						spend.merklePathSiblingsHex = siblings;
						console.log('✅ Using new witness format with merkle path!');
					} else {
						if (siblings.length > 0 && siblings.length !== ORCHARD_MERKLE_DEPTH) {
							console.warn(`[ShieldedTx] Witness merkle path incomplete for note ${note.commitment}: ${siblings.length}/${ORCHARD_MERKLE_DEPTH} siblings. Falling back to orchardTreeHex.`);
						}
						// ⚠️ Fallback: Send serialized tree for server-side parsing
						spend.orchardTreeHex = note.orchard_tree;
						spend.actionIndexInBlock = note.action_index_in_block;
						console.log('⚠️ Using fallback orchardTreeHex format');
					}
					
					console.log('📋 Mapped spend:', JSON.stringify(spend, null, 2));
					return spend;
				});
			const spendPools = countSpendsByPool(spends);
			if (spendPools.orchard && spendPools.ironwood) {
				console.log(
					`🪵 Mixed-pool spend: ${spendPools.orchard} Orchard + ${spendPools.ironwood} Ironwood note(s)`
				);
			}
			const shieldedInputTotalZatoshis = spends.reduce(
				(sum: number, spend: any) => sum + Number(spend.valueZatoshis || 0),
				0
			);
			let requiredTransparentZatoshis = useTransparentInputs
				? Math.max(0, requiredZatoshis - shieldedInputTotalZatoshis)
				: requiredZatoshis;

			if (!useTransparentInputs) {
				// Prepare optional PCZT JSON (client-side) to assist server builder
				// Calculate total input and determine change output requirement (ZIP-230 requires explicit outputs)
				const totalInputZatoshis = spends.reduce((sum: bigint, spend: any) => sum + BigInt(spend.valueZatoshis || 0), 0n);

				if (sendAll && totalInputZatoshis > 0n) {
					const availableForSend = Number(totalInputZatoshis) - feeZatoshis;
					if (availableForSend <= 0) {
						throw new Error(`Insufficient funds for fee. Inputs=${totalInputZatoshis}, fee=${feeZatoshis}`);
					}
					if (commissionEnabled) {
						amountZatoshis = Math.floor(availableForSend / (1 + commissionRateBps / 10000));
					} else {
						amountZatoshis = availableForSend;
					}
					amountZec = amountZatoshis / ZATOSHIS_PER_ZEC;
					commissionZatoshis = commissionEnabled
						? Math.max(1, Math.floor((amountZatoshis * commissionRateBps) / 10000))
						: 0;
					requiredZatoshis = amountZatoshis + feeZatoshis + commissionZatoshis;
					requiredZatoshisBig = BigInt(requiredZatoshis);
					emitStatus(`Sending all: ${amountZec.toFixed(8)} ZEC (fee: ${(feeZatoshis / ZATOSHIS_PER_ZEC).toFixed(8)} ZEC).`);
					console.log(`📤 sendAll resolved: amount=${amountZatoshis}, fee=${feeZatoshis}, commission=${commissionZatoshis}, total=${requiredZatoshis}`);
				}

				const requiredTotal = requiredZatoshisBig;

				if (totalInputZatoshis < requiredTotal) {
					throw new Error(`Insufficient note value. Inputs=${totalInputZatoshis} required=${requiredTotal}`);
				}

				const changeZatoshis = totalInputZatoshis - requiredTotal;
				if (changeZatoshis > 0n) {
					if (changeZatoshis > BigInt(Number.MAX_SAFE_INTEGER)) {
						throw new Error('Change amount exceeds JS safe integer range. Split notes differently.');
					}
					const changeNumber = Number(changeZatoshis);
					console.log(`   Change required: ${changeNumber / ZATOSHIS_PER_ZEC} ZEC (${changeZatoshis} zatoshis)`);
					changeUnifiedAddress = fromAddress;
				}
			}

			if (useTransparentInputs && requiredTransparentZatoshis > 0) {
				emitStatus('Selecting transparent UTXOs for shielding...');
				const utxoResponse = await this.walletScanner.getUtxos(transparentAddress || '');
				if (!utxoResponse?.success) {
					throw new Error('Failed to fetch transparent UTXOs for shielding');
				}

				const utxosRaw = Array.isArray(utxoResponse?.data?.utxos)
					? utxoResponse.data.utxos
					: [];
				if (!utxosRaw.length) {
					throw new Error('No transparent UTXOs available for shielding');
				}

				const confirmedUtxos = utxosRaw.filter((utxo: any) => {
					if (typeof utxo?.confirmations !== 'number') return true;
					return utxo.confirmations >= MIN_CONFIRMED_UTXO_CONFIRMATIONS;
				});

				let utxosToUse = confirmedUtxos;
				if (!confirmedUtxos.length) {
					emitStatus('No confirmed UTXOs found; using unconfirmed outputs...');
					utxosToUse = utxosRaw;
				}

				// Hoist normalisedUtxos so ZIP-317 re-selection can access the full pool
			const normalisedUtxos = utxosToUse.map((utxo: any) => {
					const txid = typeof utxo?.txid === 'string' ? utxo.txid.trim().toLowerCase() : '';
					if (!/^[0-9a-fA-F]{64}$/.test(txid)) {
						throw new Error('Invalid transparent UTXO txid');
					}
					const outputIndexCandidate = utxo?.vout ?? utxo?.outputIndex ?? utxo?.output_index ?? utxo?.index;
					const outputIndex = Number(outputIndexCandidate);
					if (!Number.isInteger(outputIndex) || outputIndex < 0) {
						throw new Error('Invalid transparent UTXO output index');
					}
					let satoshisCandidateRaw: unknown = utxo?.satoshis ?? utxo?.value;
					if (satoshisCandidateRaw === undefined || satoshisCandidateRaw === null) {
						satoshisCandidateRaw = Math.round(Number(utxo?.amount || 0) * ZATOSHIS_PER_ZEC);
					}
					const satoshisCandidate = Number(satoshisCandidateRaw);
					if (!Number.isFinite(satoshisCandidate) || satoshisCandidate <= 0) {
						throw new Error('Invalid transparent UTXO amount');
					}
					const scriptHex = normaliseScriptHex(
						utxo?.script ||
						utxo?.scriptPubKey ||
						utxo?.script_pub_key
					) || bytesToHex(toZcashOutputScript(transparentAddress || ''));
					if (!scriptHex) {
						throw new Error('Transparent UTXO scriptPubKey missing');
					}
					return {
						txid,
						vout: outputIndex,
						satoshis: satoshisCandidate,
						scriptPubKey: scriptHex
					};
				});

				allNormalisedUtxos = normalisedUtxos;

				// sendAll: use ALL UTXOs and compute amount from total inputs
				const targetTransparent = sendAll ? Number.MAX_SAFE_INTEGER : requiredTransparentZatoshis;
				const { selected, total } = selectTransparentUtxos(normalisedUtxos, targetTransparent);

				if (sendAll) {
					const totalAllInputs = shieldedInputTotalZatoshis + total;
					const availableForSend = totalAllInputs - feeZatoshis;
					if (availableForSend <= 0) {
						throw new Error(`Insufficient funds for fee. Inputs=${totalAllInputs}, fee=${feeZatoshis}`);
					}
					if (commissionEnabled) {
						amountZatoshis = Math.floor(availableForSend / (1 + commissionRateBps / 10000));
					} else {
						amountZatoshis = availableForSend;
					}
					amountZec = amountZatoshis / ZATOSHIS_PER_ZEC;
					commissionZatoshis = commissionEnabled
						? Math.max(1, Math.floor((amountZatoshis * commissionRateBps) / 10000))
						: 0;
					requiredZatoshis = amountZatoshis + feeZatoshis + commissionZatoshis;
					requiredZatoshisBig = BigInt(requiredZatoshis);
					requiredTransparentZatoshis = Math.max(0, requiredZatoshis - shieldedInputTotalZatoshis);
					emitStatus(`Sending all: ${amountZec.toFixed(8)} ZEC (fee: ${(feeZatoshis / ZATOSHIS_PER_ZEC).toFixed(8)} ZEC).`);
					console.log(`📤 sendAll (transparent path) resolved: amount=${amountZatoshis}, fee=${feeZatoshis}, commission=${commissionZatoshis}, total=${requiredZatoshis}`);
				}

				const transparentPubkeyHex = resolveTransparentPubkeyHex();
				if (!transparentPubkeyHex) {
					throw new Error(
						'Spending transparent funds needs the wallet\'s transparent public key. ' +
						'The server checks it against each UTXO\'s script before building. ' +
						'Pass transparentPublicKeyHex, or send from shielded funds only.'
					);
				}

				const buildTransparentInputsPayload = (utxos: any[]) => utxos.map((utxo: any) => ({
					txid: utxo.txid,
					vout: utxo.vout,
					scriptPubKey: utxo.scriptPubKey,
					valueZatoshis: utxo.satoshis,
					sequence: 0xffffffff,
					sighashType: 1,
					address: transparentAddress,
					pubkeyHex: transparentPubkeyHex
				}));

				if (sendAll) {
					// Every UTXO is an input and there is no transparent change, so
					// the shape is already final; the sendAll recalc below re-resolves
					// the amount if the fee moves.
					transparentInputsPayload = buildTransparentInputsPayload(selected);
				} else {
					// ── ZIP-317 fixed point: fee ↔ UTXO selection ↔ change ─────
					// The fee depends on the transaction shape, but the shape
					// depends on the fee (a higher fee can need another input, and
					// the change output can appear or disappear). The previous
					// single-pass version could declare a fee for a shape whose
					// change was computed at a LOWER fee — the server then saw
					// inputs < outputs + fee and rejected the build.
					const hasOrchardBundle = spends.length > 0 || outputsPayload.length > 0;
					const orchardOutputEstimate = outputsPayload.length + (hasOrchardBundle && !sendAll ? 1 : 0);
					const recipientTransparentOutputs = transparentOutputsPayload
						.filter((o: any) => o.address !== transparentAddress);
					let selectedUtxos = selected;
					let selectedTotal = total;
					let changeZats = 0;
					let includeChange = false;
					let feeConverged = false;
					const MAX_FEE_SHAPE_ITERATIONS = 10;
					for (let iteration = 0; iteration < MAX_FEE_SHAPE_ITERATIONS; iteration++) {
						requiredZatoshis = amountZatoshis + feeZatoshis + commissionZatoshis;
						requiredZatoshisBig = BigInt(requiredZatoshis);
						requiredTransparentZatoshis = Math.max(0, requiredZatoshis - shieldedInputTotalZatoshis);
						if (selectedTotal < requiredTransparentZatoshis) {
							// Throws when the wallet genuinely cannot cover amount + fee.
							({ selected: selectedUtxos, total: selectedTotal } =
								selectTransparentUtxos(allNormalisedUtxos, requiredTransparentZatoshis));
						}
						changeZats = selectedTotal - requiredTransparentZatoshis;
						includeChange = changeZats > TRANSPARENT_DUST_THRESHOLD_ZATOSHIS;
						const newFee = calculateZip317Fee({
							transparentInputs: selectedUtxos.length,
							transparentOutputs: recipientTransparentOutputs.length + (includeChange ? 1 : 0),
							orchardSpends: spendPools.orchard,
							ironwoodSpends: spendPools.ironwood,
							orchardOutputs: orchardOutputEstimate,
						});
						// Converged once the declared fee covers the shape minimum.
						// (A slightly-above-minimum fee stays balanced because the
						// change is always recomputed from the declared fee.)
						if (newFee <= feeZatoshis) {
							feeConverged = true;
							break;
						}
						console.log(`💰 ZIP-317 fee (shape iteration ${iteration + 1}): ${feeZatoshis} → ${newFee} zatoshis`);
						feeZatoshis = newFee;
					}
					if (!feeConverged) {
						throw new Error(
							'Fee calculation did not settle after repeated attempts — the wallet UTXOs may be too fragmented. Try a slightly different amount.'
						);
					}
					if (!includeChange && changeZats > 0) {
						// Sub-dust remainder: absorb into the fee so accounting is exact.
						feeZatoshis += changeZats;
						console.log(`⚠️ Dust change (${changeZats} zatoshis) absorbed into fee → feeZatoshis=${feeZatoshis}`);
					}
					requiredZatoshis = amountZatoshis + feeZatoshis + commissionZatoshis;
					requiredZatoshisBig = BigInt(requiredZatoshis);
					transparentInputsPayload = buildTransparentInputsPayload(selectedUtxos);
					transparentOutputsPayload = [...recipientTransparentOutputs];
					if (includeChange) {
						transparentOutputsPayload.push({
							address: transparentAddress,
							valueZatoshis: changeZats,
							userAddress: transparentAddress
						});
					}
					console.log(
						`💰 Fee/shape fixed point: fee=${feeZatoshis}, transparentIn=${selectedUtxos.length} (${selectedTotal} zats), ` +
						`change=${includeChange ? changeZats : 0}${!includeChange && changeZats > 0 ? ' (absorbed)' : ''}`
					);
				}
			}

			// ── ZIP-317 fee recalculation ──────────────────────────────────
			// Now that we know the full transaction shape, compute the real fee.
			{
				// Mirror the backend fee floor: assume a change output whenever a
				// shielded bundle is present, except on a sweep, which leaves no
				// change by construction and must not be charged for one.
				const hasOrchard = spends.length > 0 || outputsPayload.length > 0;
				const orchardOutputCount = outputsPayload.length + (hasOrchard && !sendAll ? 1 : 0);
				const newFee = calculateZip317Fee({
					transparentInputs:  transparentInputsPayload.length,
					transparentOutputs: transparentOutputsPayload.length,
					orchardSpends:      spendPools.orchard,
					ironwoodSpends:     spendPools.ironwood,
					orchardOutputs:     orchardOutputCount,
				});
				if (sendAll && newFee !== feeZatoshis) {
					// sendAll's shape is fixed (all inputs, no change output), so a
					// fee move only re-resolves the spendable amount.
					console.log(`💰 ZIP-317 fee recalculated: ${feeZatoshis} → ${newFee} zatoshis`);
					feeZatoshis = newFee;
					let totalAllInputs: number;
					if (useTransparentInputs) {
						const tpIn = transparentInputsPayload.reduce(
							(s: number, u: any) => s + (u.valueZatoshis || 0), 0);
						totalAllInputs = shieldedInputTotalZatoshis + tpIn;
					} else {
						totalAllInputs = Number(spends.reduce(
							(sum: bigint, spend: any) => sum + BigInt(spend.valueZatoshis || 0), 0n));
					}
					const availableForSend = totalAllInputs - feeZatoshis;
					if (commissionEnabled) {
						amountZatoshis = Math.floor(availableForSend / (1 + commissionRateBps / 10000));
					} else {
						amountZatoshis = Math.max(0, availableForSend);
					}
					amountZec = amountZatoshis / ZATOSHIS_PER_ZEC;
					commissionZatoshis = commissionEnabled
						? Math.max(1, Math.floor((amountZatoshis * commissionRateBps) / 10000))
						: 0;
					requiredZatoshis = amountZatoshis + feeZatoshis + commissionZatoshis;
					requiredZatoshisBig = BigInt(requiredZatoshis);
					console.log(`📤 sendAll re-resolved after ZIP-317: amount=${amountZatoshis}, fee=${feeZatoshis}, commission=${commissionZatoshis}`);
				} else if (!sendAll && newFee > feeZatoshis) {
					// The selection loops above converge the fee to the final shape,
					// so this indicates an accounting bug — refuse to ship an
					// unbalanced request rather than let the server reject it.
					throw new Error(
						`Internal fee accounting mismatch: declared ${feeZatoshis} zatoshis but the transaction shape needs ${newFee}. Please retry.`
					);
				}
				console.log(`💰 Final ZIP-317 fee: ${feeZatoshis} zatoshis (${feeZatoshis / ZATOSHIS_PER_ZEC} ZEC)`);
			}

			// ── Patch output payloads with resolved sendAll amounts ─────
			// The outputsPayload / transparentOutputsPayload were built before
			// sendAll resolved the final amountZatoshis.  Update them now.
			if (sendAll && amountZatoshis > 0) {
				for (const out of outputsPayload) {
					if (out.toUnifiedAddress === toAddress) {
						out.valueZatoshis = amountZatoshis;
					}
				}
				for (const out of transparentOutputsPayload) {
					if (out.address === toAddress) {
						out.valueZatoshis = amountZatoshis;
					}
				}
				if (commissionEnabled) {
					for (const out of outputsPayload) {
						if (out.toUnifiedAddress === commissionRecipient) {
							out.valueZatoshis = commissionZatoshis;
						}
					}
				}
				console.log(`📤 sendAll: patched output payloads → amount=${amountZatoshis}, commission=${commissionZatoshis}`);
			}

			if (amountZatoshis <= 0) {
				throw new Error(
					`Cannot send: resolved amount is ${amountZatoshis} zatoshis after fees. ` +
					`Ensure the wallet has enough funds to cover the fee (${feeZatoshis} zatoshis).`
				);
			}

			if (latestBlockHeight === null) {
				latestBlockHeight = await fetchLatestBlockHeight();
			}
			const targetHeight = latestBlockHeight ? latestBlockHeight + CONSENSUS_HEIGHT_OFFSET : undefined;
			if (targetHeight) {
				console.log(`   Target height for consensus branch id: ${targetHeight}`);
			}

			const buildPczt = () =>
				fetchWithTimeout(`${this.apiBaseUrl}/pczt/build`, {
					method: 'POST',
					headers: this.getApiHeaders(),
					body: JSON.stringify({
						network: 'main',
						seedFingerprintHex,
						accountIndex,
						ufvk,
						feeZatoshis,
						targetHeight,
						spends,
						outputs: outputsPayload,
						changeUnifiedAddress,
						transparent: (transparentInputsPayload.length || transparentOutputsPayload.length)
							? { inputs: transparentInputsPayload, outputs: transparentOutputsPayload }
							: undefined
					})
				}, PCZT_FETCH_TIMEOUT_MS);

			const maxAttempts = 3;
			let pcztBuildResponse: Response | null = null;
			let priorRetryMs: number | null = null;
			for (let attempt = 0; attempt < maxAttempts; attempt++) {
				pcztBuildResponse = await buildPczt();
				if (pcztBuildResponse.status !== 429) {
					break;
				}
				priorRetryMs = parseRateLimitRetryMs(pcztBuildResponse);
				const fallbackMs = 15000 * Math.pow(2, attempt);
				const delayMs = priorRetryMs ?? fallbackMs;
				if (attempt < maxAttempts - 1) {
					const delaySeconds = Math.max(1, Math.ceil(delayMs / 1000));
					console.warn(`⏳ PCZT build rate-limited, retrying in ${delaySeconds}s...`);
					await new Promise(resolve => setTimeout(resolve, delayMs));
				}
			}
			if (!pcztBuildResponse) {
				throw new Error('PCZT building failed: no response from server');
			}

			if (!pcztBuildResponse.ok) {
				const errorData = await safeParseJson(pcztBuildResponse);
				const errorMessage = String(errorData?.error || 'Unknown error');
				if (pcztBuildResponse.status === 429) {
					const retryMs = parseRateLimitRetryMs(pcztBuildResponse) ?? priorRetryMs;
					const retrySeconds = retryMs ? Math.ceil(retryMs / 1000) : null;
					const retryHint = retrySeconds ? ` Retry after ${retrySeconds}s.` : '';
					throw new Error(`PCZT building failed: ${errorMessage}.${retryHint}`);
				}

				// Backend fee accounting can occasionally be one ZIP-317 action higher
				// than our local estimate. If the backend reports a concrete shortfall,
				// bump fee headroom and retry once (shielded-only path).
				const shortfallMatch = errorMessage.match(/shortfall\s*=\s*(\d+)/i);
				const shortfallZatoshis = shortfallMatch ? Number(shortfallMatch[1]) : 0;
				const hasTransparentBundle = transparentInputsPayload.length > 0 || transparentOutputsPayload.length > 0;
				const canRetryWithFeeBump = Number.isFinite(shortfallZatoshis) && shortfallZatoshis > 0 && !hasTransparentBundle;

				if (!canRetryWithFeeBump) {
					throw new Error(`PCZT building failed: ${errorMessage}`);
				}

				feeZatoshis += shortfallZatoshis;
				requiredZatoshis += shortfallZatoshis;
				requiredZatoshisBig = BigInt(requiredZatoshis);
				emitStatus(
					`Server requested additional fee headroom (${(shortfallZatoshis / ZATOSHIS_PER_ZEC).toFixed(8)} ZEC). Retrying PCZT build...`
				);
				console.warn(`⚠️ PCZT fee shortfall detected (${shortfallZatoshis} zatoshis). Retrying with fee=${feeZatoshis}`);

				pcztBuildResponse = await buildPczt();
				if (!pcztBuildResponse.ok) {
					const retryErrorData = await safeParseJson(pcztBuildResponse);
					if (pcztBuildResponse.status === 429) {
						const retryMs = parseRateLimitRetryMs(pcztBuildResponse) ?? priorRetryMs;
						const retrySeconds = retryMs ? Math.ceil(retryMs / 1000) : null;
						const retryHint = retrySeconds ? ` Retry after ${retrySeconds}s.` : '';
						throw new Error(`PCZT building failed: ${retryErrorData?.error || 'Rate limit exceeded'}.${retryHint}`);
					}
					throw new Error(`PCZT building failed: ${retryErrorData?.error || errorMessage}`);
				}
			}

			const pcztResponse = await pcztBuildResponse.json();
			console.log('📦 PCZT server response:', JSON.stringify(pcztResponse, null, 2));
			
			// Normalise response field names (snake_case or camelCase)
			let pcztBase64 = pcztResponse.pczt;
			let responseFormat = pcztResponse.pcztFormat || pcztResponse.pczt_format || 'base64';
			const meta = pcztResponse.metadata || {};
			const inputsCount = meta.inputCount ?? meta.input_count;
			const outputsCount = meta.outputCount ?? meta.output_count;
			const changeValue = meta.changeValue ?? meta.change_value;

			console.log('✅ PCZT built on server');
			console.log(`   Format: ${responseFormat}`);
			if (inputsCount !== undefined && outputsCount !== undefined) {
				console.log(`   Inputs: ${inputsCount}, Outputs: ${outputsCount}`);
			}
			if (meta.fee !== undefined || changeValue !== undefined) {
				console.log(`   Fee: ${meta.fee} zatoshis, Change: ${changeValue} zatoshis`);
			}

			if (transparentInputsPayload.length > 0) {
				const transparentSighashes =
					pcztResponse.transparentSighashes ||
					pcztResponse.transparent_sighashes ||
					[];

				if (!Array.isArray(transparentSighashes) || transparentSighashes.length === 0) {
					throw new Error('Transparent inputs present but server did not return sighashes for signing');
				}

				emitStatus('Signing transparent inputs...');

				let signFn: (sighashHex: string, sighashType: number) => Promise<{ pubkeyHex: string; signatureHex: string }>;

				if (hasSignFunction) {
					signFn = async (sighashHex: string, sighashType: number) => {
						const result = await transparentSignFunction!(sighashHex);
						const derBytes = hexToBytes(result.signatureDerHex);
						const withType = new Uint8Array([...derBytes, sighashType & 0xff]);
						return { pubkeyHex: result.pubkeyHex, signatureHex: bytesToHex(withType) };
					};
				} else {
					let keyPair: { privateKeyBytes: Uint8Array; publicKeyBytes: Uint8Array; privateKeyHex: string; publicKeyHex: string };
					if (hasMnemonic) {
						keyPair = deriveTransparentKeyPair(mnemonic);
					} else if (hasTransparentKey) {
						const privBytes = hexToBytes(transparentPrivateKeyHex!);
						const pubBytes = secp256k1.getPublicKey(privBytes, true);
						keyPair = {
							privateKeyBytes: privBytes,
							publicKeyBytes: pubBytes,
							privateKeyHex: transparentPrivateKeyHex!,
							publicKeyHex: bytesToHex(pubBytes),
						};
					} else {
						throw new Error('Transparent input signing requires a recovery phrase, private key, or a transparent sign function. Seed-derived and vault-cosign wallets can only send from shielded funds unless a transparent signer is provided.');
					}
					signFn = async (sighashHex: string, sighashType: number) => {
						const signature = await secp256k1.sign(hexToBytes(String(sighashHex)), keyPair.privateKeyBytes, {
							lowS: true,
							prehash: false,
						});
						const signatureDer = signature.toDERRawBytes();
						const signatureBytes = new Uint8Array([...signatureDer, sighashType & 0xff]);
						return { pubkeyHex: keyPair.publicKeyHex, signatureHex: bytesToHex(signatureBytes) };
					};
				}

				const signatures = await Promise.all(transparentSighashes.map(async (entry: any) => {
					const sighashHex = entry.sighashHex || entry.sighash_hex || entry.sighash;
					const sighashType = Number(entry.sighashType ?? entry.sighash_type ?? 1);
					if (!sighashHex) {
						throw new Error('Transparent sighash missing from server response');
					}
					const result = await signFn(sighashHex, sighashType);
					return { index: entry.index, ...result };
				}));

				const applyResponse = await fetchWithTimeout(`${this.apiBaseUrl}/pczt/apply-transparent-signatures`, {
					method: 'POST',
					headers: this.getApiHeaders(),
					body: JSON.stringify({
						pczt: pcztBase64,
						signatures
					})
				}, PCZT_FETCH_TIMEOUT_MS);

				if (!applyResponse.ok) {
					const errorData = await safeParseJson(applyResponse);
					throw new Error(`Transparent signature apply failed: ${errorData?.error || 'Unknown error'}`);
				}

				const signedResponse = await applyResponse.json();
				if (!signedResponse?.success || !signedResponse?.pczt) {
					throw new Error(signedResponse?.error || 'Transparent signature apply failed');
				}

				pcztBase64 = signedResponse.pczt;
				responseFormat = signedResponse.pcztFormat || signedResponse.pczt_format || responseFormat;
				emitStatus('Transparent inputs signed. Continuing with shielded signing...');
			}

			// 8. Sign PCZT client-side (keys never leave client!)
			emitStatus('Signing PCZT in browser...');
			console.log('Step 6: Signing PCZT client-side...');
			
			// Validate we have the PCZT data
			if (!pcztBase64) {
				throw new Error('Server did not return PCZT data. Check server logs.');
			}
			
			// Default to base64 if format not specified
			const format = responseFormat || 'base64';
			console.log(`   Decoding PCZT as ${format}...`);
			console.log(`   PCZT data (first 100 chars): ${pcztBase64.substring(0, 100)}...`);
			
			const unsignedPcztBytes = Buffer.from(pcztBase64, format === 'hex' ? 'hex' : 'base64');
			console.log(`   PCZT bytes length: ${unsignedPcztBytes.length}`);
			
			console.log('   Parsing PCZT bytes with WASM...');
			let unsignedPczt;
			try {
				unsignedPczt = Pczt.from_bytes(unsignedPcztBytes);
				console.log('   ✅ PCZT parsed successfully');
			} catch (parseError) {
				console.error('   ❌ PCZT parsing failed:', parseError);
				console.error('   First 20 bytes (hex):', Buffer.from(unsignedPcztBytes.slice(0, 20)).toString('hex'));
				throw new Error(`PCZT parsing failed: ${parseError.message}. This suggests a version mismatch between server-side PCZT builder and client-side WASM.`);
			}
			
			const network = this.network === 'main' ? 'main' : 'test';
			let signedPcztBase64: string;

			if (useFrostSapling) {
				// ── Sapling FROST co-sign path ────────────────────────────
				emitStatus('Requesting Sapling sighash from server...');
				const saplingSighashResp = await fetch(`${this.walletScannerBaseUrl}/pczt/sapling-sighash`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ pczt: pcztBase64, pcztFormat: format }),
				});
				if (saplingSighashResp.ok) {
					const saplingData = await saplingSighashResp.json();
					if (!saplingData?.success) {
						throw new Error(saplingData?.error || 'Sapling sighash extraction failed');
					}
					const saplingSighashHex: string = saplingData.saplingSighash;
					const saplingSpendCount: number = Number(saplingData.saplingSpendCount) || 0;
					const saplingAlphas: string[] = Array.isArray(saplingData.saplingAlphas)
						? saplingData.saplingAlphas
						: [];

					if (saplingSpendCount > 1) {
						throw new Error(
							'Sapling FROST: multiple spends in one transaction are not supported yet ' +
							`(server reported ${saplingSpendCount} spends).`,
						);
					}
					if (saplingSpendCount < 1) {
						throw new Error('Sapling FROST: server reported no Sapling spends');
					}

					if (saplingSighashHex && saplingSighashHex.length === 64) {
						emitStatus('Starting Sapling FROST co-signing ceremony...');
						const saplingSignatures: string[] = [];
						for (let i = 0; i < saplingSpendCount; i++) {
							emitStatus(`Sapling FROST signing spend ${i + 1} of ${saplingSpendCount}...`);
							const alpha = saplingAlphas[i] || '';
							const sig = await saplingFrostSignFunction!(saplingSighashHex, alpha);
							saplingSignatures.push(sig);
						}

						emitStatus('Submitting Sapling FROST signatures to server...');
						const applyResp = await fetch(`${this.walletScannerBaseUrl}/pczt/apply-sapling-frost`, {
							method: 'POST',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({
								pczt: pcztBase64,
								pcztFormat: format,
								saplingSignatures,
								network,
							}),
						});
						if (!applyResp.ok) {
							const errText = await applyResp.text().catch(() => 'unknown');
							throw new Error(`Server failed to apply Sapling FROST signatures: ${errText}`);
						}
						const result = await applyResp.json();
						if (!result?.success || !result.pczt) {
							throw new Error(result?.error || 'Sapling FROST apply returned no PCZT');
						}
						pcztBase64 = result.pczt;
						console.log('Sapling FROST signatures applied');
					}
				} else {
					const errText = await saplingSighashResp.text().catch(() => 'unknown');
					throw new Error(`Server failed to extract Sapling sighash: ${errText}`);
				}
			}

			if (useFrostOrchard) {
				// ── FROST co-sign path ─────────────────────────────────────
				// Orchard actions must be signed via the relay ceremony.
				// Ask the server to extract the Orchard sighash from the PCZT,
				// run the FROST ceremony, then submit signatures for prove+broadcast.
				emitStatus('Requesting Orchard sighash from server...');
				const sighashResp = await fetch(`${this.walletScannerBaseUrl}/pczt/orchard-sighash`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ pczt: pcztBase64, pcztFormat: format }),
				});
				if (!sighashResp.ok) {
					const errText = await sighashResp.text().catch(() => 'unknown');
					throw new Error(`Server failed to extract Orchard sighash: ${errText}`);
				}
				const sighashData = await sighashResp.json();
				const orchardSighashHex: string = sighashData.orchardSighash;
				const orchardActionCountFromServer: number = sighashData.orchardActionCount || 0;
				const orchardAlphasHex: string[] = Array.isArray(sighashData.orchardAlphas)
					? sighashData.orchardAlphas
					: [];
				const orchardFrostActionIndices: number[] = Array.isArray(sighashData.orchardFrostActionIndices)
					? sighashData.orchardFrostActionIndices
					: [];
				const orchardActionTotal: number = typeof sighashData.orchardActionTotal === 'number'
					? sighashData.orchardActionTotal
					: orchardActionCountFromServer;
				const orchardFvkAks: string[] = Array.isArray(sighashData.orchardFvkAks)
					? sighashData.orchardFvkAks
					: [];
				// The server now returns the IO-finalized PCZT (dummies pre-signed)
				// — we must use that for the apply step, otherwise dummies look
				// unsigned and the server treats them as needing FROST.
				const finalizedPcztFromServer: string | undefined =
					typeof sighashData.pczt === 'string' && sighashData.pczt.length > 0
						? sighashData.pczt
						: undefined;
				const finalizedPcztFormat: string =
					typeof sighashData.pcztFormat === 'string' && sighashData.pcztFormat.length > 0
						? sighashData.pcztFormat
						: 'base64';

				if (sighashData.success === false) {
					throw new Error(
						`Server failed to extract Orchard sighash: ${sighashData.error || 'unknown error'}`
					);
				}
				if (!orchardSighashHex || orchardSighashHex.length !== 64) {
					throw new Error('Server returned invalid Orchard sighash (expected 64 hex chars / 32 bytes)');
				}

				const normaliseHex = (s: string) => (s || '').trim().toLowerCase();
				const orchardGroupPublicHex = typeof orchardFrostGroupPublicHex === 'string'
					&& /^[0-9a-fA-F]{64}$/.test(orchardFrostGroupPublicHex)
					? orchardFrostGroupPublicHex.toLowerCase()
					: undefined;

				if (orchardActionCountFromServer === 0) {
					console.log(`🔐 FROST co-sign: no real Orchard spends to sign (${orchardActionTotal} dummy actions); skipping ceremony`);
				} else if (orchardAlphasHex.length !== orchardActionCountFromServer) {
					throw new Error(
						`Server returned ${orchardAlphasHex.length} Orchard alphas but ${orchardActionCountFromServer} actions need signing. ` +
						'Upgrade the orchard-scanner binary so extract-orchard-sighash returns per-action alphas.'
					);
				}
				for (let i = 0; i < orchardAlphasHex.length; i++) {
					if (!orchardAlphasHex[i] || orchardAlphasHex[i].length !== 64) {
						throw new Error(`Server returned invalid Orchard alpha at index ${i} (expected 64 hex chars / 32 bytes)`);
					}
				}

				// ── PRE-SIGN VERIFICATION ──────────────────────────────────
				// Before asking the cosigner to sign anything, confirm that
				// every PCZT action we're about to sign was built with an
				// Orchard FVK whose `ak` equals our FROST `group_public`.
				// If not, bail out with a clear message (surface to UI),
				// because no signature produced by this FROST bundle can
				// ever verify against the PCZT's `rk`.
				if (orchardActionCountFromServer > 0 && orchardGroupPublicHex && orchardFvkAks.length === orchardActionCountFromServer) {
					const mismatches: Array<{ index: number; fvkAk: string }> = [];
					for (let i = 0; i < orchardFvkAks.length; i++) {
						const fvkAk = normaliseHex(orchardFvkAks[i]);
						if (!fvkAk || fvkAk.length !== 64) continue;
						if (fvkAk !== orchardGroupPublicHex) {
							mismatches.push({ index: orchardFrostActionIndices[i] ?? i, fvkAk });
						}
					}
					if (mismatches.length > 0) {
						const detail = mismatches
							.map((m) => `action ${m.index}: ak=${m.fvkAk.slice(0, 16)}…`)
							.join(', ');
						const msg =
							`Orchard FROST keys do not match this wallet's UFVK ` +
							`(group_public=${orchardGroupPublicHex.slice(0, 16)}…, but ${detail}). ` +
							`The PCZT was built with a different Orchard FVK than the one ` +
							`your FROST keyshare bundle derives. Rebuild the PCZT from the ` +
							`merged UFVK or regenerate the FROST Orchard keys so both ` +
							`devices share the same bundle. No signature can verify until ` +
							`this is fixed.`;
						emitStatus('Aborting: Orchard FROST keyshare does not match PCZT FVK.');
						throw new Error(msg);
					}
				}

				emitStatus('Starting Orchard FROST co-signing ceremony...');
				console.log(`🔐 FROST co-sign: sighash=${orchardSighashHex.slice(0, 16)}..., actionsToSign=${orchardActionCountFromServer}/${orchardActionTotal}, alphas=${orchardAlphasHex.length}, indices=[${orchardFrostActionIndices.join(',')}], fvkAks=[${orchardFvkAks.map(a => a.slice(0,8)).join(',')}]`);

				const frostSignatures: string[] = [];
				for (let i = 0; i < orchardActionCountFromServer; i++) {
					const pcztActionIndex = orchardFrostActionIndices[i] ?? i;
					emitStatus(`Orchard FROST signing action ${i + 1} of ${orchardActionCountFromServer}...`);
					const sig = await orchardFrostSignFunction!(
						orchardSighashHex,
						orchardAlphasHex[i],
						pcztActionIndex,
						orchardActionCountFromServer,
					);
					frostSignatures.push(sig);
				}

				emitStatus('Submitting FROST signatures to server for proving...');
				if (orchardGroupPublicHex) {
					console.log(`🔐 Sending FROST group_public for server verification: ${orchardGroupPublicHex.slice(0, 16)}...`);
				} else {
					console.warn('⚠️ No FROST group_public provided; server cannot cross-check against PCZT fvk.ak');
				}
				// Prefer the server's IO-finalized PCZT for the apply step; fall
				// back to the pre-finalized PCZT for legacy servers that don't
				// return one.
				const applyPczt = finalizedPcztFromServer ?? pcztBase64;
				const applyPcztFormat = finalizedPcztFromServer ? finalizedPcztFormat : format;
				const applyResp = await fetch(`${this.walletScannerBaseUrl}/pczt/apply-orchard-frost`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						pczt: applyPczt,
						pcztFormat: applyPcztFormat,
						orchardSignatures: frostSignatures,
						orchardFrostActionIndices: orchardFrostActionIndices.length === frostSignatures.length
							? orchardFrostActionIndices
							: undefined,
						seedFingerprint: seedFingerprint
							? Buffer.from((seedFingerprint as any).to_bytes()).toString('hex')
							: undefined,
						network,
						...(orchardGroupPublicHex ? { orchardGroupPublic: orchardGroupPublicHex } : {}),
					}),
				});
				const applyResultRaw = await applyResp.text();
				let applyResult: any = null;
				try {
					applyResult = JSON.parse(applyResultRaw);
				} catch {
					// fall through — applyResult stays null
				}
				if (!applyResp.ok) {
					const serverMsg = (applyResult && typeof applyResult.error === 'string')
						? applyResult.error
						: (applyResultRaw || 'unknown');
					throw new Error(`Server failed to apply FROST signatures: ${serverMsg}`);
				}
				if (!applyResult || applyResult.success === false || !applyResult.pczt) {
					const serverMsg = (applyResult && typeof applyResult.error === 'string')
						? applyResult.error
						: (applyResultRaw || 'apply-orchard-frost returned no PCZT');
					throw new Error(`Server failed to apply FROST signatures: ${serverMsg}`);
				}
				signedPcztBase64 = applyResult.pczt;
				console.log('✅ PCZT signed via Orchard FROST co-sign ceremony');
			} else {
				// ── Standard local signing with seed-derived USK ──────────
				const signedPczt = await pczt_sign(network, unsignedPczt, usk, seedFingerprint);
				const signedPcztBytes = signedPczt.serialize();
				signedPcztBase64 = Buffer.from(signedPcztBytes).toString('base64');
				console.log('✅ PCZT signed client-side (local USK)');
			}
			console.log(`   Expected Orchard outputs: ${outputsPayload.length} (+ change), Transparent inputs: ${transparentInputsPayload.length}`);

			// ── Pre-broadcast safety checks ──────────────────────────────────
			// Guard against silently dropped Orchard bundles or runaway fees.
			const MAX_FEE_ZATOSHIS = 100_000; // 0.001 ZEC — well above any ZIP-317 fee
			// `outputsPayload` already includes the commission output; the change
			// output is added server-side and is not counted here.
			const expectedShieldedOutputCount = outputsPayload.length;
			const serverReportedFee = meta.fee ?? meta.feeZatoshis;
			const orchardActionCount = meta.orchardActionCount ?? meta.orchard_action_count;
			const ironwoodActionCount = meta.ironwoodActionCount ?? meta.ironwood_action_count;

			// Helper: ask the user to confirm a high fee, or hard-block if no callback
			const confirmOrAbort = async (feeAmount: number, reason: string) => {
				const feeZec = (feeAmount / ZATOSHIS_PER_ZEC).toFixed(8);
				if (onFeeWarning) {
					const accepted = await onFeeWarning({ feeZatoshis: feeAmount, feeZec, reason });
					if (!accepted) {
						throw new Error(`Transaction cancelled by user: ${reason}`);
					}
					console.log(`⚠️ User accepted high fee: ${feeAmount} zatoshis — ${reason}`);
				} else {
					throw new Error(
						`SAFETY ABORT: ${reason} Fee: ${feeAmount} zatoshis (${feeZec} ZEC). ` +
						`Transaction NOT broadcast.`
					);
				}
			};

			// 1. Fee cap — warn/block if server or local fee exceeds safe limit
			if (typeof serverReportedFee === 'number' && serverReportedFee > MAX_FEE_ZATOSHIS) {
				await confirmOrAbort(serverReportedFee,
					`Server-reported fee (${serverReportedFee} zatoshis) exceeds the safe ` +
					`maximum of ${MAX_FEE_ZATOSHIS} zatoshis. This may indicate a missing ` +
					`Orchard bundle or incorrect change calculation.`
				);
			}
			if (feeZatoshis > MAX_FEE_ZATOSHIS) {
				await confirmOrAbort(feeZatoshis,
					`Calculated fee (${feeZatoshis} zatoshis) exceeds the safe maximum ` +
					`of ${MAX_FEE_ZATOSHIS} zatoshis.`
				);
			}

			// 2. Bundle presence — always hard-block (never let the user override
			//    a silently dropped bundle).
			//
			//    Which bundle carries what changed at NU6.3. Outputs are always
			//    minted in Ironwood now, so declared outputs say nothing about
			//    whether an Orchard bundle should exist; that follows from the
			//    spends. Checking outputs against the Orchard action count — as
			//    this did — fails every Ironwood-only spend, where there is
			//    correctly no Orchard bundle at all.
			if (expectedShieldedOutputCount > 0
				&& typeof ironwoodActionCount === 'number'
				&& ironwoodActionCount === 0) {
				throw new Error(
					`SAFETY ABORT: Expected ${expectedShieldedOutputCount} shielded output(s) ` +
					`but the PCZT has 0 Ironwood actions. The Ironwood bundle was not built. ` +
					`Transaction NOT broadcast. Please report this issue.`
				);
			}
			if (spendPools.orchard > 0
				&& typeof orchardActionCount === 'number'
				&& orchardActionCount === 0) {
				throw new Error(
					`SAFETY ABORT: Spending ${spendPools.orchard} Orchard note(s) but the PCZT ` +
					`has 0 Orchard actions. The Orchard bundle was not built. ` +
					`Transaction NOT broadcast. Please report this issue.`
				);
			}
			if (spendPools.ironwood > 0
				&& typeof ironwoodActionCount === 'number'
				&& ironwoodActionCount === 0) {
				throw new Error(
					`SAFETY ABORT: Spending ${spendPools.ironwood} Ironwood note(s) but the PCZT ` +
					`has 0 Ironwood actions. The Ironwood bundle was not built. ` +
					`Transaction NOT broadcast. Please report this issue.`
				);
			}
			if (expectedShieldedOutputCount > 0 && ironwoodActionCount === undefined) {
				console.warn('⚠️ Server did not report ironwoodActionCount — cannot verify Ironwood bundle presence');
			}

			// 3. Server fee vs local fee — warn/block if divergence is large
			if (typeof serverReportedFee === 'number' && Math.abs(serverReportedFee - feeZatoshis) > 20_000) {
				await confirmOrAbort(serverReportedFee,
					`Server fee (${serverReportedFee} zatoshis) differs from locally ` +
					`calculated fee (${feeZatoshis} zatoshis) by more than 20,000 zatoshis. ` +
					`This may indicate a missing Orchard bundle or accounting error.`
				);
			}

			// 4. Log accounting summary for diagnostics
			{
				const totalTransparentIn = transparentInputsPayload.reduce(
					(s: number, u: any) => s + (u.valueZatoshis || 0), 0);
				const totalTransparentOut = transparentOutputsPayload.reduce(
					(s: number, u: any) => s + (u.valueZatoshis || 0), 0);
				console.log(
					`🔒 Pre-broadcast accounting: ` +
					`transparentIn=${totalTransparentIn}, shieldedIn=${shieldedInputTotalZatoshis}, ` +
					`orchardOut=${amountZatoshis}+${commissionZatoshis}, transparentOut=${totalTransparentOut}, ` +
					`fee=${feeZatoshis}, serverFee=${serverReportedFee ?? 'n/a'}, ` +
					`orchardActions=${orchardActionCount ?? 'n/a'}, ironwoodActions=${ironwoodActionCount ?? 'n/a'}`
				);
			}

			// 9. Broadcast via server
			emitStatus('Broadcasting transaction...');
			console.log('Step 7: Broadcasting transaction...');
			pendingNullifiersForAttempt = notes
				.map((note: any) => this.getNoteNullifierHex(note))
				.map((nf) => normaliseNullifierHex(nf))
				.filter((nf): nf is string => Boolean(nf));
			if (pendingNullifiersForAttempt.length > 0 && ufvk) {
				markPendingNullifiersForUfvk(ufvk, pendingNullifiersForAttempt);
				console.log(`🕒 Marked ${pendingNullifiersForAttempt.length} note(s) as pending`);
			}
			const broadcastResponse = await fetchWithTimeout(`${this.apiBaseUrl}/pczt/broadcast`, {
				method: 'POST',
				headers: this.getApiHeaders(),
				body: JSON.stringify({
					signedPczt: signedPcztBase64,
					format: 'base64'
				})
			}, PCZT_FETCH_TIMEOUT_MS);

			if (!broadcastResponse.ok) {
				const errorData = await broadcastResponse.json();
				throw new Error(`Broadcasting failed: ${errorData.error || 'Unknown error'}`);
			}

			const { txid } = await broadcastResponse.json();
			console.log(`✅ Transaction broadcast successfully!`);
			console.log(`   TxID: ${txid}`);
			emitStatus(`Broadcast complete. TxID: ${txid}`);

			// Update the cache to reflect spent notes rather than nuking it entirely.
			// Keeping unspent notes in cache prevents needing a full rescan just to
			// see the remaining balance (which the scanner may not have yet).
			if (cacheKey) {
				const spentNulls = new Set(pendingNullifiersForAttempt);
				const existing = noteCache.get(cacheKey);
				if (existing && spentNulls.size > 0) {
					const remaining = existing.notes.filter((note: any) => {
						const nf = normaliseNullifierHex(this.getNoteNullifierHex(note));
						return !(nf && spentNulls.has(nf));
					});
					noteCache.set(cacheKey, {
						...existing,
						notes: remaining,
						timestamp: Date.now(),
					});
					_persistNoteCache();
					console.log(
						`💾 Updated cache: removed ${existing.notes.length - remaining.length} spent ` +
						`note(s), ${remaining.length} remain`
					);
				} else {
					_deleteCacheEntry(cacheKey);
					_persistNoteCache();
					clearNoteCacheForAddress(fromAddress);
					console.log('🗑️ Cleared cached notes after broadcast');
				}
			}

			return txid;

		} catch (error) {
			if ((error as any)?.code === 'ORCHARD_DKG_REBUILD') throw error;
			console.error('❌ Shielded transaction failed:', error);
			const errorMessage = String(error?.message || '').toLowerCase();
			const isDuplicateNullifier = DUPLICATE_NULLIFIER_MARKERS.some((marker) => errorMessage.includes(marker));
			if (pendingNullifiersForAttempt.length > 0 && ufvk && !isDuplicateNullifier) {
				clearPendingNullifiersForUfvk(ufvk, pendingNullifiersForAttempt);
			}
			// A consensus double-spend rejection is proof the note is gone: the
			// nullifier is already on chain. Marking it pending only suppresses it
			// for the current attempt, and a later scan can merge it straight back
			// in — so evict it from the cache outright and let the next retry
			// through without a forced rescan.
			if (isDuplicateNullifier && cacheKey) {
				const rejected = new Set(extractRejectedNullifiers(String(error?.message || '')));
				const existing = noteCache.get(cacheKey);
				if (rejected.size > 0 && existing?.notes?.length) {
					const remaining = existing.notes.filter((note: any) => {
						const nf = normaliseNullifierHex(this.getNoteNullifierHex(note));
						return !(nf && rejected.has(nf));
					});
					const dropped = existing.notes.length - remaining.length;
					if (dropped > 0) {
						noteCache.set(cacheKey, { ...existing, notes: remaining, timestamp: Date.now() });
						_persistNoteCache();
						if (ufvk) _dispatchBalanceEvent(ufvk);
						console.log(`🗑️ Evicted ${dropped} already-spent note(s) named by the rejection`);
					}
				}
			}

			// Provide helpful error messages based on error type
			if (error.message?.includes('leafPosition') || error.message?.includes('merklePathSiblingsHex')) {
				// Witness data format issue
				throw new Error(
					`⚠️ WITNESS DATA MISSING\n\n` +
					`The wallet scanner returned notes but witness data is incomplete.\n\n` +
					`Check console logs to see what was received.\n` +
					`The scanner should return:\n` +
					`  • anchor_root (merkle tree root)\n` +
					`  • leaf_position (note position in tree)\n` +
					`  • merkle_path.siblings[] (authentication path)\n\n` +
					`If you see 'Using fallback orchardTreeHex format', the scanner needs to be updated.\n` +
					`Backend team: Ensure witness=true returns unpacked witness data.`
				);
			} else if (error.message?.includes('Invalid PCZT request')) {
				// Data format validation error
				const contextHint = transparentFallbackEnabled
					? 'The PCZT builder rejected the mixed (transparent + orchard) request. Ensure the API accepts empty spends when transparent inputs are provided.'
					: 'The wallet scanner returned notes in an unexpected format. This is likely a compatibility issue between the scanner and API.';
				throw new Error(
					`PCZT data format error: ${error.message}\n\n` +
					`${contextHint}\n` +
					`Check the console for detailed request payloads.`
				);
			} else if (error.message?.includes('AnchorMismatch')) {
				throw new Error(
					`Server-side PCZT building failed due to AnchorMismatch.\n\n` +
					`This usually means notes with different anchor roots were selected.\n` +
					`Retry the send, or rescan with a narrower birthday height so notes share an anchor.\n` +
					`Details: ${error.message}`
				);
			} else if (isDuplicateNullifier) {
				throw new Error(
					'Shielded note already spent or pending in mempool. The spent note has been dropped from the wallet\'s cache — retry the send; if it repeats, rescan.'
				);
			} else if (error.message?.includes('PCZT building failed') || error.message?.includes('not yet implemented')) {
				throw new Error(
					`Server-side PCZT building failed.\n\n` +
					`Error: ${error.message}\n\n` +
					`Check API logs for more details.`
				);
		} else if (error?.code === 'USER_CANCELLED' || abortSignal?.aborted) {
			throw new Error('Shielded send cancelled by user.');
		} else if (error.name === 'AbortError' && !error.message?.toLowerCase?.().includes('shielded')) {
			// Only treat genuine fetch AbortErrors (no custom message) as a
			// timeout. Errors whose `.message` mentions "shielded" came from
			// our own logic (e.g. apply-orchard-frost failure, FROST key
			// mismatch) — those must surface as-is so the user sees the
			// real reason, not a bogus "timeout" banner.
			throw new Error('Shielded transaction timed out while contacting the PCZT service. Please retry or increase the timeout.');
		} else if (error.message?.includes('No spendable notes')) {
				throw new Error('No shielded funds available to spend. The unified address needs to receive shielded transactions first.');
			} else if (error.message?.includes('Insufficient')) {
				throw new Error(`Insufficient shielded funds. Requested: ${amountZec} ZEC`);
			} else {
				throw new Error(`Shielded transaction failed: ${error.message}`);
			}
		}
	}

	/**
	 * Helper: Get anchor root hex from a note (if present)
	 */
	private getAnchorRootHex(note: any): string | null {
		const anchorRoot = note?.anchor_root ?? note?.anchorRootHex ?? note?.anchor;
		if (typeof anchorRoot !== 'string') {
			return null;
		}
		const trimmed = anchorRoot.trim();
		return trimmed ? trimmed : null;
	}

	private getNoteNullifierHex(note: any): string | null {
		const candidate =
			note?.note_nullifier ??
			note?.noteNullifier ??
			note?.nullifier ??
			note?.nullifier_hex ??
			note?.nullifierHex ??
			note?.note?.nullifier ??
			note?.note?.note_nullifier ??
			note?.note?.nullifierHex ??
			note?.note?.nullifier_hex;
		return normaliseNullifierHex(candidate);
	}

	private getNoteRhoHex(note: any): string | null {
		const candidate =
			note?.rho ??
			note?.rho_hex ??
			note?.note?.rho ??
			note?.note?.rho_hex ??
			note?.note_plaintext?.rho ??
			note?.note_plaintext?.rho_hex;
		return normaliseNullifierHex(candidate);
	}

	/**
	 * Select every spendable note that can go into one transaction, for a sweep.
	 *
	 * `selectNotesForSpend` stops as soon as the running total covers what is
	 * being sent and prefers the fewest notes, which is right for a payment and
	 * wrong for "send everything": asked to sweep, it would pick the one note
	 * that covers the fee and leave the rest behind. Pool migrations are sweeps,
	 * so they need this instead.
	 *
	 * Notes still have to share an anchor root, so the answer is the largest
	 * single anchor group rather than literally every note. Whatever is left
	 * over is swept by a second transaction once its witnesses catch up.
	 */
	private selectAllNotesForSweep(notes: any[]): { selectedNotes: any[]; anchorRoot?: string } {
		if (!Array.isArray(notes) || notes.length === 0) {
			throw new Error('No spendable notes available for selection.');
		}

		const anchorGroups = new Map<string, any[]>();
		const seenNullifiers = new Set<string>();
		let largestUnwitnessed: any = null;

		for (const note of notes) {
			if (note?.spent || note?.is_spent || note?.isSpent) continue;
			const noteNullifier = this.getNoteNullifierHex(note);
			if (!noteNullifier || seenNullifiers.has(noteNullifier)) continue;
			seenNullifiers.add(noteNullifier);
			if (BigInt(note?.value || 0) <= 0n) continue;

			const anchorRoot = this.getAnchorRootHex(note);
			if (anchorRoot && hasFullMerklePathWitness(note)) {
				const group = anchorGroups.get(anchorRoot) || [];
				group.push(note);
				anchorGroups.set(anchorRoot, group);
			} else if (note?.orchard_tree) {
				if (!largestUnwitnessed || Number(note.value || 0) > Number(largestUnwitnessed.value || 0)) {
					largestUnwitnessed = note;
				}
			}
		}

		let best: { anchorRoot: string; notes: any[]; total: bigint } | null = null;
		for (const [anchorRoot, group] of anchorGroups) {
			const total = group.reduce((sum: bigint, note: any) => sum + BigInt(note?.value || 0), 0n);
			if (!best || total > best.total) {
				best = { anchorRoot, notes: group, total };
			}
		}

		if (best) {
			return { selectedNotes: best.notes, anchorRoot: best.anchorRoot };
		}
		if (largestUnwitnessed) {
			// No witnessed group: fall back to the tree-hex path, one note only.
			return { selectedNotes: [largestUnwitnessed] };
		}
		throw new Error('No spendable notes available for selection.');
	}

	/**
	 * Select notes that share the same anchor root to avoid AnchorMismatch.
	 */
	private selectNotesForSpend(
		notes: any[],
		requiredZatoshis: bigint
	): { selectedNotes: any[]; anchorRoot?: string } {
		if (!Array.isArray(notes) || notes.length === 0) {
			throw new Error('No spendable notes available for selection.');
		}
		if (requiredZatoshis <= 0n) {
			throw new Error('Required amount must be greater than 0.');
		}

		type Candidate = {
			anchorRoot: string;
			selectedNotes: any[];
			total: bigint;
			change: bigint;
		};

		const anchorGroups = new Map<string, any[]>();
		const singleNoteCandidates: { note: any; value: bigint; anchorRoot?: string }[] = [];
		const seenNullifiers = new Set<string>();

		for (const note of notes) {
			const isSpent = Boolean(note?.spent || note?.is_spent || note?.isSpent);
			if (isSpent) {
				continue;
			}
			const noteNullifier = this.getNoteNullifierHex(note);
			if (!noteNullifier) {
				continue;
			}
			if (seenNullifiers.has(noteNullifier)) {
				continue;
			}
			seenNullifiers.add(noteNullifier);
			const value = BigInt(note?.value || 0);
			if (value <= 0n) {
				continue;
			}

			const anchorRoot = this.getAnchorRootHex(note);
			const hasWitness = hasFullMerklePathWitness(note);
			const hasTree = Boolean(note?.orchard_tree);
			if (!hasWitness && !hasTree) {
				continue;
			}

			if (anchorRoot && hasWitness) {
				const group = anchorGroups.get(anchorRoot) || [];
				group.push(note);
				anchorGroups.set(anchorRoot, group);
			}

			if (value >= requiredZatoshis) {
				singleNoteCandidates.push({ note, value, anchorRoot: anchorRoot || undefined });
			}
		}

		const candidates: Candidate[] = [];
		for (const [anchorRoot, group] of anchorGroups) {
			const sorted = group
				.slice()
				.sort((a, b) => Number(b?.value || 0) - Number(a?.value || 0));
			let total = 0n;
			const selected: any[] = [];
			for (const note of sorted) {
				total += BigInt(note?.value || 0);
				selected.push(note);
				if (total >= requiredZatoshis) {
					break;
				}
			}
			if (total >= requiredZatoshis) {
				candidates.push({
					anchorRoot,
					selectedNotes: selected,
					total,
					change: total - requiredZatoshis
				});
			}
		}

		if (candidates.length) {
			candidates.sort((a, b) => {
				const countDiff = a.selectedNotes.length - b.selectedNotes.length;
				if (countDiff !== 0) return countDiff;
				if (a.change === b.change) return 0;
				return a.change < b.change ? -1 : 1;
			});
			const best = candidates[0];
			return { selectedNotes: best.selectedNotes, anchorRoot: best.anchorRoot };
		}

		if (singleNoteCandidates.length) {
			singleNoteCandidates.sort((a, b) => {
				if (a.value === b.value) return 0;
				return a.value < b.value ? -1 : 1;
			});
			const best = singleNoteCandidates[0];
			return { selectedNotes: [best.note], anchorRoot: best.anchorRoot };
		}

		throw new Error('No set of spendable notes share the same anchor root with sufficient funds.');
	}

	/**
	 * Helper: Convert zatoshis to ZEC string
	 */
	private zatoshisToZecString(zatoshis: bigint): string {
		const zec = Number(zatoshis) / 100000000;
		return zec.toFixed(8);
	}

	private static parseNoteValueZatoshis(note: any): number {
		const rawValue =
			note?.value ??
			note?.value_zat ??
			note?.valueZat ??
			note?.amount ??
			note?.amount_zat ??
			note?.amountZat ??
			note?.note_value ??
			note?.noteValue ??
			0;

		if (typeof rawValue === 'number') {
			return Number.isFinite(rawValue) ? rawValue : 0;
		}
		if (typeof rawValue === 'bigint') {
			return Number(rawValue);
		}
		if (typeof rawValue === 'string') {
			const trimmed = rawValue.trim();
			if (!trimmed) return 0;
			try {
				if (/^0x[0-9a-f]+$/i.test(trimmed)) {
					return Number(BigInt(trimmed));
				}
				if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
					if (trimmed.includes('.')) {
						return Math.round(Number(trimmed) * ZATOSHIS_PER_ZEC);
					}
					return Number(trimmed);
				}
			} catch (_parseError) {
				return 0;
			}
		}
		return 0;
	}

	/**
	 * Reverse a hex string byte-by-byte (e.g. "aabb" -> "bbaa").
	 * Zcash tx hashes from lightwalletd are in internal (LE) byte order;
	 * explorers and APIs expect display (BE) order.
	 */
	private static reverseHexBytes(hex: string): string {
		const clean = hex.replace(/\s/g, '');
		if (clean.length !== 64 || !/^[0-9a-f]+$/i.test(clean)) return hex;
		const pairs: string[] = [];
		for (let i = 0; i < clean.length; i += 2) {
			pairs.push(clean.substring(i, i + 2));
		}
		return pairs.reverse().join('');
	}

	private normalizeRawNote(note: any): ShieldedNote {
		let memo: string | undefined;
		const rawMemo = note.memo || note.memo_text || note.memoText || note.memo_hex || note.memoHex;
		if (typeof rawMemo === 'string' && rawMemo.length > 0) {
			if (/^[0-9a-f]+$/i.test(rawMemo) && rawMemo.length > 2) {
				try {
					const decoded = Buffer.from(rawMemo, 'hex').toString('utf8').replace(/\0+$/, '');
					memo = decoded.length > 0 ? decoded : undefined;
				} catch { memo = undefined; }
			} else {
				memo = rawMemo;
			}
		}

		let txHash = note.tx_hash || note.txHash || note.txid || note.transactionHash || '';
		const isAlreadyDisplay = note.tx_hash_format === 'display' || note.txHashFormat === 'display';
		if (typeof txHash === 'string' && txHash.length === 64 && !isAlreadyDisplay) {
			txHash = ShieldedTransactionClient.reverseHexBytes(txHash);
		}

		return {
			txHash,
			blockHeight: Number(note.block_height ?? note.blockHeight ?? note.height ?? 0),
			value: ShieldedTransactionClient.parseNoteValueZatoshis(note),
			pool: normaliseNotePool(note.pool),
			nullifier: this.getNoteNullifierHex(note) || note.nullifier || note.nullifierHex || undefined,
			commitment: note.commitment || note.cm || note.note_commitment || undefined,
			memo
		};
	}

	/**
	 * Get cached notes from the most recent balance scan (no extra network call).
	 * Falls back to a fresh scanUfvkImmediate if cache is empty.
	 */
	async getSpendableNotes(
		mnemonic: string,
		accountIndex: number = 0,
		birthdayHeight?: number,
		preExportedUfvk?: string
	): Promise<ShieldedNote[]> {
		console.log('🔍 Getting spendable shielded notes...');

		try {
			const ufvk = preExportedUfvk || await this.exportUfvkLocal(mnemonic, accountIndex);

			const cached = noteCache.get(ufvk);
			if (cached && Array.isArray(cached.notes) && cached.notes.length > 0) {
				const age = Date.now() - (cached.timestamp || 0);
				if (age < NOTE_CACHE_TTL) {
					const notes: ShieldedNote[] = cached.notes.map((note: any) => this.normalizeRawNote(note));
					console.log(`✅ Found ${notes.length} spendable notes (from cache, age ${Math.round(age / 1000)}s)`);
					return notes;
				}
			}

			const scanResult = await this.walletScanner.scanUfvkImmediate({
				ufvk,
				birthdayHeight: birthdayHeight || 0
			});

			if (!scanResult.success || !scanResult.data?.notes) {
				console.log('⚠️ Immediate scan returned no notes');
				return [];
			}

			const notes: ShieldedNote[] = scanResult.data.notes.map((note: any) => this.normalizeRawNote(note));

			console.log(`✅ Found ${notes.length} spendable notes (from immediate scan)`);
			return notes;

		} catch (error) {
			console.error('❌ Failed to get spendable notes:', error);
			return [];
		}
	}

	/**
	 * Validate if an address can be used for shielded transactions
	 */
	async validateShieldedAddress(address: string): Promise<{ isValid: boolean; type: string; canReceiveShielded: boolean }> {
		const validation = await this.webzjsClient.validateAddress(address);
		
		const canReceiveShielded = 
			validation.type === 'unified' || 
			validation.type === 'sapling' || 
			validation.type === 'orchard';

		return {
			...validation,
			canReceiveShielded
		};
	}

	clearAllCachedNotes(): void {
		noteCache.clear();
		pendingNullifiersByUfvk.clear();
		// Note cache is in-memory only since the May 2026 privacy fix,
		// but sweep any legacy entry an older build might have left
		// behind so a fresh load can't inherit it.
		sweepLegacyShieldedLocalStorage();
		console.log('🗑️ Cleared all cached notes (memory)');
	}
}

/**
 * Singleton instance for global use
 */
let globalShieldedClient: ShieldedTransactionClient | null = null;

/**
 * Get or create the global shielded transaction client
 */
export function getCachedBalance(ufvk: string): { transparent: bigint; shielded: bigint } | null {
	if (!ufvk) return null;
	const cached = noteCache.get(ufvk);
	if (!cached) return null;
	const shielded = cached.notes.reduce(
		(sum: bigint, note: any) => sum + BigInt(note?.value || 0), 0n
	);
	return { transparent: 0n, shielded };
}

/**
 * Read-only copy of the cached shielded notes for a UFVK. History /
 * diary UIs use this to list incoming transactions (tx hash, block
 * height, value, decrypted memo) without triggering a scan. Session
 * memory only — empty until a balance scan has run.
 */
export function getCachedNotesForUfvk(ufvk: string): any[] {
	if (!ufvk) return [];
	const cached = noteCache.get(ufvk);
	if (!cached || !Array.isArray(cached.notes)) return [];
	return cached.notes.map((note: any) => ({ ...note }));
}

export function getShieldedTransactionClient(
	network: string = 'main',
	lightwalletdUrl?: string,
	walletScannerUrl?: string,
	apiBaseUrl?: string,
	apiKey?: string
): ShieldedTransactionClient {
	if (!globalShieldedClient) {
		globalShieldedClient = new ShieldedTransactionClient(
			network,
			lightwalletdUrl,
			walletScannerUrl,
			apiBaseUrl,
			apiKey
		);
	} else if (apiKey !== undefined) {
		globalShieldedClient.setApiKey(apiKey);
	}
	return globalShieldedClient;
}

